import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Layers, Plus, Trash2, Send, FileDown, RefreshCw, Loader2, CheckCircle2,
  Building2, CircleDot, Zap, ClipboardCheck, AlertTriangle, Save, Upload, ClipboardList, XCircle, ScanLine, Landmark, Search, Sparkles, Mail, Check, X, ShieldCheck, Banknote,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (v) =>
  Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Loans above this face value are flagged for a human to verify the amount before
// assembly (guards against data-entry typos, e.g. a mistaken $20M payoff).
const LARGE_FACE = 5000000;

const EMPTY_BANK = { name: "", fax_number: "", contact: "", city: "", state: "", country: "", email: "", phone: "", aba: "", swift: "", instrument: "", lc_status: "", tenor: "", recourse: "", notes: "" };

export default function BatchSalePanel() {
  const token = localStorage.getItem("th_admin_token") || "";
  const hdr = { headers: { Authorization: `Bearer ${token}` } };

  const [settings, setSettings] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loans, setLoans] = useState([]);
  const [readyCount, setReadyCount] = useState(0);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [bankForm, setBankForm] = useState(EMPTY_BANK);
  const [editingBankId, setEditingBankId] = useState(null);
  const [savingBank, setSavingBank] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedingVerified, setSeedingVerified] = useState(false);
  const [coverBusy, setCoverBusy] = useState(null);
  const [replyDays, setReplyDays] = useState(5);
  const [lookupId, setLookupId] = useState(null);
  const [bankFilter, setBankFilter] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyProgress, setVerifyProgress] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [readyOnly, setReadyOnly] = useState(false);
  const [expandedPkg, setExpandedPkg] = useState(null);
  const [ocrPkg, setOcrPkg] = useState(null);
  const [accBank, setAccBank] = useState(null);
  const [resendPkg, setResendPkg] = useState(null);
  const [resendBankId, setResendBankId] = useState({});
  const [resending, setResending] = useState(null);
  const [selectedPkgs, setSelectedPkgs] = useState([]);
  const [excluded, setExcluded] = useState(() => new Set());
  const toggleExcluded = (id) => setExcluded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [bulkBankId, setBulkBankId] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [failedOnly, setFailedOnly] = useState(false);
  const [treasuryEdit, setTreasuryEdit] = useState(null);
  const [savingTreasury, setSavingTreasury] = useState(null);
  const [standstillResend, setStandstillResend] = useState({});
  const [resendingStandstill, setResendingStandstill] = useState(null);
  const [bulkTreasuryOpen, setBulkTreasuryOpen] = useState(false);
  const [savingBulkTreasury, setSavingBulkTreasury] = useState(false);
  const [formsSettings, setFormsSettings] = useState(null);
  const [formsOpen, setFormsOpen] = useState(false);
  const [savingForms, setSavingForms] = useState(false);
  const [testingTcmm, setTestingTcmm] = useState(false);

  const sendTestToTcmm = async () => {
    const buildIds = loans.filter((l) => l.ready_for_sale && !l.batch_sold && !excluded.has(l.id)).map((l) => l.id);
    if (buildIds.length === 0) { toast.error("Flag at least one loan 'Ready' to run a test"); return; }
    const first = loans.find((l) => l.id === buildIds[0]);
    if (!window.confirm(`Send a LIVE end-to-end test to the Treasury Collateral (TCMM) desk using "${first?.borrower_name}"?\n\nThis faxes the Outright Sale + Treasury package + Treasury forms AND a counterparty standstill — ALL to TCMM (+1 866-707-6574). No real bank or lender is contacted, and the loan is NOT marked sold.`)) return;
    setTestingTcmm(true);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/test-tcmm`, { app_id: buildIds[0] }, hdr);
      const rec = r.data;
      toast.success(`Test ${rec.batch_no} sent to TCMM — package ${rec.fax_status || "queued"}`);
      const css = rec.counterparty_standstills || [];
      if (css.length) toast.message(`Standstill → TCMM: ${css[0].status} · Treasury pkg bundled: ${rec.treasury_pkg_count}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "TCMM test failed"); }
    finally { setTestingTcmm(false); }
  };

  const loadFormsSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/treasury/forms/settings`, hdr);
      setFormsSettings(r.data.settings || null);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadFormsSettings(); }, [loadFormsSettings]);

  const saveFormsSettings = async () => {
    setSavingForms(true);
    try {
      const r = await axios.put(`${API}/admin/treasury/forms/settings`, formsSettings, hdr);
      setFormsSettings(r.data.settings);
      toast.success("Treasury forms signatories saved");
      setFormsOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save signatories"); }
    finally { setSavingForms(false); }
  };

  const loadAuctions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/treasury/auctions?days=180&limit=250`, hdr);
      return r.data.securities || [];
    } catch { toast.error("Could not load live Treasury auctions"); return []; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTreasury = (l) => setTreasuryEdit((id) => (id === l.id ? null : l.id));

  const downloadTreasuryForms = async (loan) => {
    try {
      const r = await axios.get(`${API}/admin/treasury/forms/${loan.id}/download`, { ...hdr, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not open Treasury forms"); }
  };

  const efaxTreasuryForms = async (loan) => {
    const to = window.prompt(`eFax the auto-filled Treasury forms (FS 1010 + TCMM 225 pledge + redemption coupon) for ${loan.borrower_name}. Destination fax (E.164, e.g. +1866…):`, loan.lender_fax || "");
    if (!to || !to.trim()) return;
    try {
      const r = await axios.post(`${API}/admin/treasury/forms/${loan.id}/efax`, { to: to.trim() }, hdr);
      toast.success(`Treasury forms eFaxed to ${r.data.to} (${r.data.fax?.status || "queued"})`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Treasury forms eFax failed"); }
  };

  const saveTreasury = async (loan, settings) => {
    setSavingTreasury(loan.id);
    try {
      await axios.post(`${API}/admin/borrower-applications/${loan.id}/motion-data`,
        { treasury_doc_settings: settings }, hdr);
      toast.success(`Treasury Repo data saved for ${loan.borrower_name} — it will bundle into the batch`);
      setTreasuryEdit(null);
      const e = await axios.get(`${API}/admin/batch-sale/eligible`, hdr);
      setLoans(e.data.loans || []);
      setReadyCount(e.data.ready_count || 0);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save Treasury data"); }
    finally { setSavingTreasury(null); }
  };

  const saveBulkTreasury = async (settings) => {
    const targets = loans.filter((l) => l.ready_for_sale && !l.batch_sold && !excluded.has(l.id));
    if (targets.length === 0) { toast.error("No included (Ready) loans to attach Treasury data to"); return; }
    if (!window.confirm(`Apply this Treasury Repo data to all ${targets.length} included borrower(s)?`)) return;
    setSavingBulkTreasury(true);
    try {
      await Promise.all(targets.map((l) =>
        axios.post(`${API}/admin/borrower-applications/${l.id}/motion-data`, { treasury_doc_settings: settings }, hdr)));
      toast.success(`Treasury Repo data attached to ${targets.length} borrower(s)`);
      setBulkTreasuryOpen(false);
      const e = await axios.get(`${API}/admin/batch-sale/eligible`, hdr);
      setLoans(e.data.loans || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk attach failed"); }
    finally { setSavingBulkTreasury(false); }
  };

  const resendStandstill = async (pkg, row) => {
    const fax = (standstillResend[`${pkg.id}:${row.app_id}`] || "").trim();
    setResendingStandstill(`${pkg.id}:${row.app_id}`);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/packages/${pkg.id}/standstill/${row.app_id}/resend`,
        { fax: fax || undefined }, hdr);
      const st = r.data?.result?.status;
      st === "failed"
        ? toast.error(`Standstill to ${row.borrower} failed: ${r.data?.result?.reason || "error"}`)
        : toast.success(`Standstill re-sent to ${row.borrower}'s counterparty (${st})`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not re-send standstill"); }
    finally { setResendingStandstill(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, e, p, v] = await Promise.all([
        axios.get(`${API}/admin/batch-sale/settings`, hdr),
        axios.get(`${API}/admin/batch-sale/banks`, hdr),
        axios.get(`${API}/admin/batch-sale/eligible`, hdr),
        axios.get(`${API}/admin/batch-sale/packages`, hdr),
        axios.get(`${API}/admin/batch-sale/banks/bulk-verify/status`, hdr),
      ]);
      setSettings(s.data);
      setBanks(b.data.banks || []);
      setLoans(e.data.loans || []);
      setReadyCount(e.data.ready_count || 0);
      setPackages(p.data.packages || []);
      setSuggestions(v.data.suggestions || []);
    } catch (err) {
      toast.error("Could not load batch sale data");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchSettings = async (patch) => {
    try {
      const r = await axios.put(`${API}/admin/batch-sale/settings`, patch, hdr);
      setSettings(r.data);
      toast.success("Settings saved");
    } catch { toast.error("Could not save settings"); }
  };

  const toggleReady = async (id, ready) => {
    setLoans((prev) => prev.map((l) => (l.id === id ? { ...l, ready_for_sale: ready } : l)));
    try {
      await axios.post(`${API}/admin/batch-sale/loans/${id}/ready`, { ready }, hdr);
      const e = await axios.get(`${API}/admin/batch-sale/eligible`, hdr);
      setLoans(e.data.loans || []);
      setReadyCount(e.data.ready_count || 0);
    } catch { toast.error("Could not update flag"); load(); }
  };

  const unsellLoan = async (l) => {
    if (!window.confirm(`Return ${l.borrower_name} to the "Ready for Outright Sale" pool? This un-marks the loan as sold and removes it from any batch package it's in, so you can re-batch and re-send it (use this when a batch fax failed).`)) return;
    try {
      const r = await axios.post(`${API}/admin/batch-sale/loans/${l.id}/unsell`, {}, hdr);
      const note = r.data.packages_deleted ? ` · ${r.data.packages_deleted} empty package(s) removed` : "";
      toast.success(`${l.borrower_name} returned to Ready${note}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not return loan to Ready"); }
  };

  const returnAllSold = async () => {
    const soldCount = loans.filter((l) => l.batch_sold).length;
    if (soldCount === 0) { toast.message("No sold loans to return"); return; }
    if (!window.confirm(`Return ALL ${soldCount} sold loan(s) to the "Ready for Outright Sale" pool? This un-marks them as sold and detaches them from their batch packages (empty packages are removed). Use this to recover a whole failed batch at once.`)) return;
    try {
      const r = await axios.post(`${API}/admin/batch-sale/loans/return-all-sold`, {}, hdr);
      const note = r.data.packages_deleted ? ` · ${r.data.packages_deleted} empty package(s) removed` : "";
      toast.success(`Returned ${r.data.returned} loan(s) to Ready${note}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not return sold loans"); }
  };

  const saveBank = async () => {
    if (!bankForm.name.trim()) {
      toast.error("Bank name is required");
      return;
    }
    setSavingBank(true);
    try {
      if (editingBankId) await axios.put(`${API}/admin/batch-sale/banks/${editingBankId}`, bankForm, hdr);
      else await axios.post(`${API}/admin/batch-sale/banks`, bankForm, hdr);
      setBankForm(EMPTY_BANK); setEditingBankId(null);
      const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr);
      setBanks(b.data.banks || []);
      toast.success((editingBankId ? "Bank updated" : "Bank added") + (bankForm.fax_number.trim() ? "" : " · fax pending — verify before eFaxing"));
    } catch { toast.error("Could not save bank"); } finally { setSavingBank(false); }
  };

  const seedBanks = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/banks/seed`, {}, hdr);
      toast.success(`Seeded ${r.data.added} bank(s)${r.data.skipped ? ` · ${r.data.skipped} already present` : ""} — fax numbers pending, verify before use`);
      const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr);
      setBanks(b.data.banks || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Seed failed"); } finally { setSeeding(false); }
  };

  const seedVerifiedBanks = async () => {
    setSeedingVerified(true);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/banks/seed-verified`, {}, hdr);
      toast.success(`Loaded ${r.data.added} verified bank(s)${r.data.backfilled ? ` · ${r.data.backfilled} updated` : ""}${r.data.fax_verified ? ` · ${r.data.fax_verified} eFax-verified` : ""}`);
      const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr);
      setBanks(b.data.banks || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Load failed"); } finally { setSeedingVerified(false); }
  };

  const autoResolveFax = async (bankId) => {
    const bank = banks.find((x) => x.id === bankId);
    if (!bank || (bank.fax_number || "").trim()) return;
    try {
      const r = await axios.post(`${API}/admin/batch-sale/banks/lookup`, { name: bank.name }, hdr);
      const s = r.data.suggested || {};
      if (!s.fax_number) { toast.warning(`No fax found online for ${bank.name} — add it manually or it will be printed & mailed.`); return; }
      await axios.put(`${API}/admin/batch-sale/banks/${bankId}`, {
        name: bank.name, fax_number: s.fax_number, contact: bank.contact || s.phone || "",
        city: bank.city || s.city || "", state: bank.state || s.state || "",
        aba: bank.aba || s.aba || "", swift: bank.swift || s.swift || "", notes: bank.notes || s.address || "",
      }, hdr);
      const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr);
      setBanks(b.data.banks || []);
      toast.success(`Auto-filled eFax for ${bank.name}: ${s.fax_number}`);
    } catch (err) { console.warn("auto fax lookup unavailable", err); }
  };

  const flagAllReady = async () => {
    const pending = loans.filter((l) => !l.ready_for_sale && !l.batch_sold).length;
    if (!window.confirm(`Flag all ${pending} un-flagged approved loan(s) as Ready for Outright Sale?`)) return;
    try {
      const r = await axios.post(`${API}/admin/batch-sale/loans/ready-all`, {}, hdr);
      const e = await axios.get(`${API}/admin/batch-sale/eligible`, hdr);
      setLoans(e.data.loans || []);
      toast.success(`Flagged ${r.data.flagged} loan(s) Ready for Outright Sale`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to flag loans ready"); }
  };

  const confirmFax = async (bankId) => {
    try {
      await axios.post(`${API}/admin/batch-sale/banks/${bankId}/confirm-fax`, { confirmed: true }, hdr);
      const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr);
      setBanks(b.data.banks || []);
      toast.success("Fax marked verified");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not confirm fax"); }
  };

  const setDiscount = async (pct) => {
    try {
      const r = await axios.put(`${API}/admin/batch-sale/settings`, { discount_pct: Number(pct) }, hdr);
      setSettings(r.data);
    } catch { /* ignore */ }
  };

  const lookupFax = async (bank) => {
    setLookupId(bank.id);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/banks/lookup`, { name: bank.name }, hdr);
      const s = r.data.suggested || {};
      setBankForm({
        name: bank.name,
        fax_number: s.fax_number || bank.fax_number || "",
        contact: bank.contact || s.phone || "",
        city: bank.city || s.city || "",
        state: bank.state || s.state || "",
        aba: bank.aba || s.aba || "",
        swift: bank.swift || s.swift || "",
        notes: bank.notes || s.address || "",
      });
      setEditingBankId(bank.id);
      if (s.fax_number) toast.success(`${r.data.note} Review & save to confirm.`);
      else toast.warning(r.data.note || "No fax found — enter it manually and save.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Lookup failed"); } finally { setLookupId(null); }
  };

  const pollVerify = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/batch-sale/banks/bulk-verify/status`, hdr);
      setVerifyProgress(r.data.progress || null);
      setSuggestions(r.data.suggestions || []);
      return r.data.progress?.running;
    } catch { return false; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bulkVerify = async () => {
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/banks/bulk-verify`, {}, hdr);
      if (!r.data.started) { toast.info(r.data.message || "Nothing to verify"); setVerifying(false); return; }
      toast.success(`Verifying ${r.data.total} fax-pending bank(s)… suggestions will appear below`);
      const tick = async () => {
        const running = await pollVerify();
        if (running) setTimeout(tick, 3000);
        else { setVerifying(false); const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr); setBanks(b.data.banks || []); toast.success("Bulk verify complete — review suggestions below"); }
      };
      setTimeout(tick, 2500);
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk verify failed"); setVerifying(false); }
  };

  const applySuggestion = async (bankId, accept) => {
    try {
      await axios.post(`${API}/admin/batch-sale/banks/${bankId}/apply-suggestion`, { accept }, hdr);
      setSuggestions((prev) => prev.filter((x) => x.id !== bankId));
      const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr);
      setBanks(b.data.banks || []);
      toast.success(accept ? "Applied to bank" : "Suggestion dismissed");
    } catch { toast.error("Could not update suggestion"); }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/digest/send`, {}, hdr);
      if (r.data.sent) toast.success(`Digest emailed to ${r.data.to} · ${r.data.pools} batch(es)`);
      else toast.warning(r.data.note || "Composed but not delivered — check email config");
    } catch (err) { toast.error(err?.response?.data?.detail || "Digest failed"); } finally { setSendingDigest(false); }
  };

  const deleteBank = async (id) => {
    try {
      await axios.delete(`${API}/admin/batch-sale/banks/${id}`, hdr);
      setBanks((prev) => prev.filter((x) => x.id !== id));
      toast.success("Bank removed");
    } catch { toast.error("Could not remove bank"); }
  };

  const importBanks = async () => {
    if (!csvText.trim()) { toast.error("Paste CSV rows first"); return; }
    setImporting(true);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/banks/import`, { csv_text: csvText }, hdr);
      toast.success(`Imported ${r.data.imported} bank(s)${r.data.errors?.length ? ` · ${r.data.errors.length} skipped` : ""}`);
      if (r.data.errors?.length) r.data.errors.slice(0, 3).forEach((e) => toast.error(e));
      setCsvText("");
      const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr);
      setBanks(b.data.banks || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); } finally { setImporting(false); }
  };

  const reconcile = async (pkgId, appId, decision) => {
    try {
      await axios.post(`${API}/admin/batch-sale/packages/${pkgId}/reconcile`, { app_id: appId, decision }, hdr);
      const p = await axios.get(`${API}/admin/batch-sale/packages`, hdr);
      setPackages(p.data.packages || []);
    } catch { toast.error("Could not update decision"); }
  };

  const uploadReturnSheet = async (pkgId, file) => {
    if (!file) return;
    setOcrPkg(pkgId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/batch-sale/packages/${pkgId}/reconcile-from-upload`, fd,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Auto-reconciled ${r.data.applied} decision(s) · ${r.data.accepted_count} accepted / ${r.data.declined_count} declined`);
      const p = await axios.get(`${API}/admin/batch-sale/packages`, hdr);
      setPackages(p.data.packages || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read the returned sheet");
    } finally { setOcrPkg(null); }
  };

  const runBatch = async (mode, send) => {
    if (!settings?.accepting_bank_id) { toast.error("Select an accepting/discounting bank first"); return; }
    const buildIds = loans.filter((l) => l.ready_for_sale && !l.batch_sold && !excluded.has(l.id)).map((l) => l.id);
    if (buildIds.length === 0) { toast.error("Select at least one 'Ready' loan to include in the batch"); return; }
    const big = loans.filter((l) => buildIds.includes(l.id) && Number(l.face_value || 0) > LARGE_FACE);
    if (big.length > 0) {
      const list = big.map((l) => `• ${l.borrower_name}: ${fmtUSD(l.face_value)}`).join("\n");
      if (!window.confirm(`⚠ LARGE FACE VALUE — please verify before assembling.\n\n${big.length} selected borrower(s) exceed ${fmtUSD(LARGE_FACE)}:\n${list}\n\nThese amounts come from each file's payoff/balance data. Continue with the batch build?`)) return;
    }
    if (send && !window.confirm(`Assemble ${buildIds.length} selected borrower(s) and eFax the bundle now to the selected bank? This sends a LIVE fax.`)) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/run`, { mode, send, app_ids: buildIds }, hdr);
      const rec = r.data;
      if (rec.status === "sent") toast.success(`Batch ${rec.batch_no} assembled & eFaxed (${rec.pool_count} borrowers)`);
      else if (rec.status === "efax_failed") toast.error(`Assembled ${rec.batch_no} but eFax failed: ${rec.fax_status}`);
      else toast.success(`Batch ${rec.batch_no} assembled (${rec.pool_count} borrowers) — review & send below`);
      if (rec.treasury_pkg_count > 0) toast.message(`Treasury package(s) integrated: ${rec.treasury_pkg_count} borrower(s)`);
      const tc = rec.treasury_collateral_copy;
      if (tc && tc.status && tc.status !== "skipped") {
        tc.status === "failed"
          ? toast.error(`Treasury Collateral copy failed: ${tc.reason || "error"}`)
          : toast.success(`Treasury Collateral desk copy sent (${tc.status})`);
      }
      const css = rec.counterparty_standstills || [];
      if (css.length) {
        const sent = css.filter((x) => x.status && !["skipped", "failed"].includes(x.status)).length;
        const skipped = css.filter((x) => x.status === "skipped").length;
        const failed = css.filter((x) => x.status === "failed").length;
        toast.message(`Counterparty standstill eFax → ${sent} sent${skipped ? `, ${skipped} skipped (no fax)` : ""}${failed ? `, ${failed} failed` : ""}`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Batch run failed");
    } finally { setRunning(false); }
  };

  const coverLetter = async (channel) => {
    if (!settings?.accepting_bank_id) { toast.error("Select an accepting/discounting bank first"); return; }
    if (readyCount === 0) { toast.error("No loans flagged 'Ready for Outright Sale'"); return; }
    const sb = banks.find((x) => x.id === settings?.accepting_bank_id);
    if (channel === "efax" && !window.confirm(`eFax the invitation-to-bid cover letter to ${sb?.name}? This sends a LIVE fax.`)) return;
    if (channel === "email" && !window.confirm(`Email the invitation-to-bid cover letter to ${sb?.name} (${sb?.email})?`)) return;
    setCoverBusy(channel);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/cover-letter`, { channel, reply_by_days: replyDays }, hdr);
      const d = r.data;
      if (channel === "download") { if (d.url) window.open(d.url, "_blank"); toast.success(`Cover letter ready · ${d.pool_count} note(s)`); }
      else if (d.sent) toast.success(`Cover letter ${channel === "efax" ? "eFaxed" : "emailed"} to ${d.to}`);
      else toast.warning("Composed but not delivered — check config");
    } catch (err) { toast.error(err?.response?.data?.detail || "Cover letter failed"); }
    finally { setCoverBusy(null); }
  };

  const refreshFaxStatus = async (pkg) => {
    if (!pkg.fax_id) { toast.error("No fax has been sent for this package yet"); return; }
    try {
      const r = await axios.post(`${API}/admin/fax/${pkg.fax_id}/refresh`, {}, hdr);
      const st = r.data.status;
      setPackages((prev) => prev.map((x) => x.id === pkg.id ? { ...x, delivery_status: st, fax_status: st, delivery_failure_reason: r.data.failure_reason } : x));
      if (st === "delivered") toast.success(`${pkg.batch_no}: delivered ✓`);
      else if (st === "failed" || st === "delivery_failed") toast.error(`${pkg.batch_no}: Telnyx reports FAILED${r.data.failure_reason ? ` — ${r.data.failure_reason}` : ""}`);
      else if (st === "expired") toast.warning(`${pkg.batch_no}: ${r.data.detail || "no longer retained by Telnyx"}`);
      else toast.message(`${pkg.batch_no}: ${st}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Refresh failed"); }
  };

  const deletePackage = async (pkg) => {
    if (!window.confirm(`Delete batch package ${pkg.batch_no}? This removes it from the list only — it does not reverse any loans already sold.`)) return;
    try {
      await axios.delete(`${API}/admin/batch-sale/packages/${pkg.id}`, hdr);
      setPackages((prev) => prev.filter((x) => x.id !== pkg.id));
      toast.success(`Deleted ${pkg.batch_no}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const efaxPackage = async (pkg) => {
    if (!window.confirm(`eFax batch ${pkg.batch_no} to ${pkg.accepting_bank?.name} (${pkg.accepting_bank?.fax_number})? This sends a LIVE fax.`)) return;
    try {
      const r = await axios.post(`${API}/admin/batch-sale/packages/${pkg.id}/efax`, {}, hdr);
      toast.success(`eFax queued (${r.data.status})`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "eFax failed"); }
  };

  const resendToBank = async (pkg) => {
    const bankId = resendBankId[pkg.id];
    if (!bankId) { toast.error("Pick a bank to re-send to"); return; }
    const bank = banks.find((b) => b.id === bankId);
    if (!window.confirm(`Re-send batch ${pkg.batch_no} to ${bank?.name} (${bank?.fax_number || "no fax"})? This sends a LIVE fax and re-assigns this batch to the new bank.`)) return;
    setResending(pkg.id);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/packages/${pkg.id}/efax`, { accepting_bank_id: bankId }, hdr);
      toast.success(`Re-sent to ${r.data.bank?.name || bank?.name} (${r.data.status})`);
      setResendPkg(null);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Re-send failed"); }
    finally { setResending(null); }
  };

  const removeLoanFromPkg = async (pkg, row) => {
    if (!window.confirm(`Remove ${row.borrower} from batch ${pkg.batch_no}? This un-marks the loan as sold and returns it to the "Ready for Outright Sale" pool.`)) return;
    try {
      const r = await axios.delete(`${API}/admin/batch-sale/packages/${pkg.id}/loans/${row.app_id}`, hdr);
      if (r.data.package_deleted) toast.success(`${row.borrower} removed — package ${pkg.batch_no} was emptied and deleted`);
      else toast.success(`${row.borrower} removed and returned to the Ready pool`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Remove failed"); }
  };

  const togglePkgSelect = (id) =>
    setSelectedPkgs((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const selectFailedPkgs = () => {
    const bad = packages.filter((p) => {
      const d = String(p.delivery_status || "").toLowerCase();
      const s = String(p.status || "").toLowerCase();
      return s === "efax_failed" || d === "failed" || d === "delivery_failed" || d === "expired";
    }).map((p) => p.id);
    setSelectedPkgs(bad);
    if (bad.length === 0) toast.message("No failed/undelivered packages found");
    else toast.success(`Selected ${bad.length} failed/undelivered package(s)`);
  };

  const bulkResend = async () => {
    if (selectedPkgs.length === 0) { toast.error("Select at least one package"); return; }
    if (!bulkBankId) { toast.error("Pick a bank to re-send to"); return; }
    const bank = banks.find((b) => b.id === bulkBankId);
    if (!window.confirm(`Re-send ${selectedPkgs.length} selected package(s) to ${bank?.name} (${bank?.fax_number || "no fax"})? This sends LIVE faxes and re-assigns each batch to this bank.`)) return;
    setBulkSending(true);
    try {
      const r = await axios.post(`${API}/admin/batch-sale/packages/bulk-efax`,
        { package_ids: selectedPkgs, accepting_bank_id: bulkBankId }, hdr);
      const { sent, failed, results } = r.data;
      if (failed === 0) toast.success(`All ${sent} package(s) re-sent to ${bank?.name}`);
      else {
        const failedNos = (results || []).filter((x) => !x.ok).map((x) => x.batch_no || x.id).join(", ");
        toast.warning(`${sent} sent, ${failed} failed → ${bank?.name}. Failed: ${failedNos}`);
      }
      setSelectedPkgs([]);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk re-send failed"); }
    finally { setBulkSending(false); }
  };

  const remit = async (pkgId, body) => {
    try {
      await axios.post(`${API}/admin/batch-sale/packages/${pkgId}/remittance`, body, hdr);
      const p = await axios.get(`${API}/admin/batch-sale/packages`, hdr);
      setPackages(p.data.packages || []);
      toast.success("Remittance updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update remittance"); }
  };

  const uploadProof = async (pkgId, appId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/batch-sale/packages/${pkgId}/remittance/${appId}/proof`, fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      const p = await axios.get(`${API}/admin/batch-sale/packages`, hdr);
      setPackages(p.data.packages || []);
      toast.success("Payoff confirmation attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to upload confirmation"); }
  };

  const saveAccreditation = async (bankId, body) => {
    try {
      await axios.post(`${API}/admin/batch-sale/banks/${bankId}/accreditation`, body, hdr);
      const b = await axios.get(`${API}/admin/batch-sale/banks`, hdr);
      setBanks(b.data.banks || []);
      setAccBank(null);
      toast.success("Accreditation saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save accreditation"); }
  };

  const mode = settings?.mode || "semi_auto";

  return (
    <section className="mb-12 scroll-mt-32" id="sec-batch-sale" data-testid="batch-sale-panel">
      <div className="mb-5 flex items-center gap-3">
        <Layers size={18} className="text-[#B8860B]" />
        <h2 className="text-2xl font-serif">Batch Sale Packages</h2>
        <button onClick={load} disabled={loading}
          className="ml-auto flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] border border-[#2A2A22] hover:border-[#B8860B] text-[#B8860B] rounded-md px-3 py-1.5 disabled:opacity-50"
          data-testid="batch-refresh-btn">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>
      <p className="text-[12px] text-[#7A7A70] mb-6 max-w-3xl">
        Consolidates every approved loan flagged <b>“Ready for Outright Sale”</b> into ONE bundle — a batch cover, a
        consolidated Consent &amp; Certification, and per-borrower Private Negotiable Instrument binders that direct the accepting
        bank to remit to each borrower’s counterparty. Auto-assembles every <b>Wednesday</b>; use the push buttons to run
        it now.
      </p>

      {/* ── Automation settings ── */}
      <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5 mb-6" data-testid="batch-settings-card">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#9A9A90] font-bold mb-3">Wednesday Automation</div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <div className="text-[11px] text-[#8A8A80] mb-1.5">Run mode</div>
            <div className="flex gap-2">
              <button onClick={() => patchSettings({ mode: "fully_auto" })}
                data-testid="batch-mode-fully"
                className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] rounded-md px-2 py-2 border transition-colors ${mode === "fully_auto" ? "border-[#C9A227] bg-[#C9A227]/15 text-[#E4C97A] font-semibold" : "border-[#2A2A22] text-[#C9C9C0] hover:border-[#C9A227]/50"}`}>
                <Zap size={13} /> Fully hands-off
              </button>
              <button onClick={() => patchSettings({ mode: "semi_auto" })}
                data-testid="batch-mode-semi"
                className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] rounded-md px-2 py-2 border transition-colors ${mode === "semi_auto" ? "border-[#3DBB8E] bg-[#3DBB8E]/15 text-[#7fe7c2] font-semibold" : "border-[#2A2A22] text-[#C9C9C0] hover:border-[#3DBB8E]/50"}`}>
                <ClipboardCheck size={13} /> Semi-auto (review)
              </button>
            </div>
            <div className="text-[10px] text-[#6A6A60] mt-1.5">
              {mode === "fully_auto" ? "Wednesday: assemble + auto-eFax to the bank." : "Wednesday: assemble + queue for your review."}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] text-[#8A8A80]">Accepting / Discounting Bank</div>
              <button onClick={() => setReadyOnly((v) => !v)} data-testid="batch-ready-only-toggle"
                className={`flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 border transition-colors ${readyOnly ? "border-[#3DBB8E] text-[#7fe7c2] bg-[#3DBB8E]/10" : "border-[#2A2A22] text-[#8A8A80]"}`}>
                {readyOnly ? <CheckCircle2 size={10} /> : <CircleDot size={10} />} eFax-ready only
              </button>
            </div>
            <select value={settings?.accepting_bank_id || ""}
              onChange={(e) => { const v = e.target.value; patchSettings({ accepting_bank_id: v }); autoResolveFax(v); }}
              data-testid="batch-accepting-bank-select"
              className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]">
              <option value="">— Select from rolodex —</option>
              {banks.filter((b) => !readyOnly || (b.fax_number || "").trim() || b.id === settings?.accepting_bank_id).map((b) => (
                <option key={b.id} value={b.id}>{b.name}{b.fax_number ? ` · ${b.fax_number}` : " · (fax pending)"}</option>
              ))}
            </select>
            <div className="text-[10px] text-[#6A6A60] mt-1.5">
              {readyOnly
                ? `Showing ${banks.filter((b) => (b.fax_number || "").trim()).length} eFax-ready bank(s). The bundle is faxed to this one bank.`
                : `Showing all ${banks.length} bank(s) from the rolodex. The bundle is delivered to this one bank.`}
            </div>
            {settings?.accepting_bank_id && (() => {
              const sb = banks.find((x) => x.id === settings.accepting_bank_id);
              if (!sb) return null;
              const accredited = !!(sb.accreditation && sb.accreditation.signed);
              const hasFax = !!(sb.fax_number || "").trim();
              return (
                <div className="mt-2" data-testid="batch-selected-bank-status">
                  <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] rounded px-1.5 py-0.5 border ${accredited ? "border-[#3DBB8E] text-[#7fe7c2] bg-[#3DBB8E]/10" : "border-[#E4B84A] text-[#E4B84A] bg-[#E4B84A]/10"}`} data-testid="batch-sel-accred">
                    {accredited ? "✓ Accredited buyer" : "⚠ Needs accreditation"}
                  </span>
                  {!accredited && (
                    <button onClick={() => setAccBank(accBank === sb.id ? null : sb.id)} data-testid="batch-sel-accredit-now"
                      className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-2 py-0.5 border border-[#E4C97A] text-[#E4C97A] hover:bg-[#E4C97A]/10">
                      <ShieldCheck size={11} /> {accBank === sb.id ? "Close" : "Accredit now"}
                    </button>
                  )}
                  <span className={`text-[10px] rounded px-1.5 py-0.5 border ${hasFax ? (sb.fax_confirmed_at ? "border-[#3DBB8E] text-[#7fe7c2] bg-[#3DBB8E]/10" : "border-[#E4B84A] text-[#E4B84A] bg-[#E4B84A]/10") : "border-[#8A8A80] text-[#8A8A80]"}`} data-testid="batch-sel-fax">
                    {hasFax ? (sb.fax_confirmed_at ? `✓ eFax verified · ${sb.fax_number}` : `eFax (unconfirmed): ${sb.fax_number}`) : "Fax pending"}
                  </span>
                  {hasFax && !sb.fax_confirmed_at && (
                    <button onClick={() => confirmFax(sb.id)} data-testid="batch-sel-confirm-fax"
                      className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-2 py-0.5 border border-[#3DBB8E] text-[#7fe7c2] hover:bg-[#3DBB8E]/10">
                      <CheckCircle2 size={11} /> Confirm fax on file
                    </button>
                  )}
                  {sb.fax_confirmed_at && (
                    <span className="text-[9.5px] text-[#6A8A7A]" data-testid="batch-sel-fax-date">verified {String(sb.fax_confirmed_at).slice(0, 10)}</span>
                  )}
                  {!hasFax && (
                    <button onClick={() => autoResolveFax(sb.id)} data-testid="batch-sel-lookup-fax"
                      className="inline-flex items-center gap-1 text-[10px] font-bold rounded px-2 py-0.5 border border-[#7fe7c2] text-[#7fe7c2] hover:bg-[#7fe7c2]/10">
                      <Search size={11} /> Look up fax
                    </button>
                  )}
                  </div>
                  {!accredited && accBank === sb.id && (
                    <AccreditationForm bank={sb} onSave={saveAccreditation} onCancel={() => setAccBank(null)} />
                  )}
                </div>
              );
            })()}
          </div>
          <div>
            <div className="text-[11px] text-[#8A8A80] mb-1.5">Weekly auto-run</div>
            <button onClick={() => patchSettings({ auto_run_enabled: !settings?.auto_run_enabled })}
              data-testid="batch-autorun-toggle"
              className={`w-full flex items-center justify-center gap-1.5 text-[11px] rounded-md px-2 py-2 border transition-colors ${settings?.auto_run_enabled ? "border-[#C9A227] bg-[#C9A227]/15 text-[#E4C97A] font-semibold" : "border-[#2A2A22] text-[#8A8A80]"}`}>
              <CircleDot size={13} /> {settings?.auto_run_enabled ? "Enabled" : "Disabled"}
            </button>
            <div className="text-[10px] text-[#6A6A60] mt-1.5">Toggle the Wednesday scheduler on/off.</div>
          </div>
        </div>

        {/* Push buttons */}
        <div className="flex flex-wrap gap-3 mt-5 pt-4 border-t border-[#2A2A22]">
          <button onClick={() => runBatch("fully_auto", true)} disabled={running}
            data-testid="batch-run-fully-btn"
            className="flex items-center gap-2 text-[12px] font-semibold bg-[#C9A227] hover:bg-[#dab63a] text-[#0A0A0A] rounded-md px-4 py-2.5 disabled:opacity-50">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Build &amp; eFax now (hands-off)
          </button>
          <button onClick={() => runBatch("semi_auto", false)} disabled={running}
            data-testid="batch-run-semi-btn"
            className="flex items-center gap-2 text-[12px] font-semibold border border-[#3DBB8E] text-[#7fe7c2] hover:bg-[#3DBB8E]/10 rounded-md px-4 py-2.5 disabled:opacity-50">
            {running ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />} Build package (review first)
          </button>
          <span className="text-[11px] text-[#8A8A80] self-center" data-testid="batch-ready-count">
            {readyCount} loan{readyCount === 1 ? "" : "s"} ready for this batch
          </span>
        </div>
        <div className="mt-2 text-[10.5px] text-[#8A8A80] leading-relaxed" data-testid="batch-flow-help">
          <strong className="text-[#E4C97A]">How the batch works:</strong> (1) flag one or more approved loans <strong>Ready for Outright Sale</strong> below · (2) pick an <strong>accepting/discounting bank</strong> from the rolodex that is <strong>accredited</strong> · (3) click <strong>Build package (review first)</strong> to assemble the bundle, or <strong>Build &amp; eFax</strong> to also fax it (the bank needs a fax number for eFax — use <strong>Look up</strong> in the rolodex to fetch it from the FDIC/directory).
        </div>
        {(() => {
          const sb = banks.find((x) => x.id === settings?.accepting_bank_id);
          const bankOk = !!(sb && sb.accreditation && sb.accreditation.signed);
          const ok = readyCount > 0 && bankOk;
          const missing = [];
          if (readyCount === 0) missing.push("flag at least one loan Ready");
          if (!bankOk) missing.push("select an accredited accepting bank");
          return (
            <div className={`mt-3 rounded-md border px-3 py-2 text-[11px] font-semibold ${ok ? "border-[#3DBB8E]/60 bg-[#3DBB8E]/10 text-[#7fe7c2]" : "border-[#E4B84A]/50 bg-[#E4B84A]/10 text-[#E4B84A]"}`} data-testid="batch-readiness-banner">
              {ok
                ? `✓ Ready to build — ${readyCount} loan(s) flagged and ${sb.name} is accredited.`
                : `Not ready yet: ${missing.join(" · ")}.`}
            </div>
          );
        })()}
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="batch-discount-control">
          <label className="text-[11px] text-[#9A9A90] font-semibold">Discount rate</label>
          <input type="number" min="0" max="99" step="0.5" value={settings ? (settings.discount_pct ?? 15) : ""} disabled={!settings}
            onChange={(e) => setSettings((s) => ({ ...(s || {}), discount_pct: e.target.value }))}
            onBlur={(e) => setDiscount(e.target.value)} data-testid="batch-discount-input"
            className="w-20 bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-2 py-1 text-[12px] text-[#ECECE4] disabled:opacity-40" />
          <span className="text-[11px] text-[#8A8A80]">% off face — buyer pays {(100 - Number(settings?.discount_pct ?? 15)).toFixed(1)}% of face value; the rest is your desk's discount margin.</span>
        </div>

        {/* Auto-sell on intake */}
        <div className="mt-3 flex items-start gap-3 rounded-md border border-[#2A2A22] bg-[#0F0F0A] px-3 py-2.5" data-testid="batch-autosell-control">
          <button onClick={() => patchSettings({ auto_sell_on_intake: !settings?.auto_sell_on_intake })}
            data-testid="batch-autosell-toggle"
            className={`shrink-0 flex items-center gap-1.5 text-[11px] rounded-md px-2.5 py-2 border transition-colors ${settings?.auto_sell_on_intake ? "border-[#C9A227] bg-[#C9A227]/15 text-[#E4C97A] font-semibold" : "border-[#2A2A22] text-[#8A8A80]"}`}>
            <CircleDot size={13} /> {settings?.auto_sell_on_intake ? "Auto-sell ON" : "Auto-sell OFF"}
          </button>
          <div className="text-[10.5px] text-[#8A8A80] leading-relaxed">
            When <strong className="text-[#E4C97A]">ON</strong>, the moment a borrower submits intake we assemble that loan's <strong>full Outright Note Sale package</strong> and send it to your <strong>default discounting bank</strong> above — <strong>email → eFax → queue for print &amp; mail</strong>. Requires a default bank that is <strong>accredited</strong>; if none is set/accredited, the loan is just flagged Ready for the weekly batch instead.
          </div>
        </div>

        {/* Purchase & Delivery Instructions — Invitation to Bid cover letter */}
        <div className="mt-5 pt-4 border-t border-[#2A2A22]" data-testid="batch-cover-letter">
          <div className="flex items-center gap-2 mb-1.5">
            <Mail size={14} className="text-[#B8860B]" />
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#9A9A90] font-bold">Invitation to Bid — Cover Letter</div>
          </div>
          <div className="text-[10.5px] text-[#8A8A80] leading-relaxed mb-2.5">
            Send a prospective discounting bank a Purchase &amp; Delivery Instructions letter first — it states the pool size, your indicative discount, and <strong>both settlement models</strong> (remit direct to each counterparty, or wire to Seutrade), and asks the bank to return its <strong>bid (discount %)</strong> and <strong>intake fax/portal</strong>. This same letter also leads every assembled batch PDF.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-[#9A9A90] font-semibold">Reply-by (business days)</label>
            <input type="number" min="1" max="30" value={replyDays}
              onChange={(e) => setReplyDays(Math.max(1, Number(e.target.value || 5)))}
              data-testid="batch-cover-replydays"
              className="w-16 bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-2 py-1 text-[12px] text-[#ECECE4]" />
            <button onClick={() => coverLetter("download")} disabled={!!coverBusy}
              data-testid="batch-cover-download"
              className="flex items-center gap-1.5 text-[11px] font-semibold border border-[#B8860B] text-[#E4C97A] hover:bg-[#B8860B]/10 rounded-md px-3 py-2 disabled:opacity-50">
              {coverBusy === "download" ? <Loader2 size={13} className="animate-spin" /> : <ClipboardList size={13} />} Preview / download
            </button>
            <button onClick={() => coverLetter("efax")} disabled={!!coverBusy}
              data-testid="batch-cover-efax"
              className="flex items-center gap-1.5 text-[11px] font-semibold border border-[#3DBB8E] text-[#7fe7c2] hover:bg-[#3DBB8E]/10 rounded-md px-3 py-2 disabled:opacity-50">
              {coverBusy === "efax" ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />} eFax to bank
            </button>
            <button onClick={() => coverLetter("email")} disabled={!!coverBusy}
              data-testid="batch-cover-email"
              className="flex items-center gap-1.5 text-[11px] font-semibold border border-[#6C8CD5] text-[#a9c1f0] hover:bg-[#6C8CD5]/10 rounded-md px-3 py-2 disabled:opacity-50">
              {coverBusy === "email" ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email to bank
            </button>
          </div>
        </div>

        {/* Weekly digest */}
        <div className="mt-5 pt-4 border-t border-[#2A2A22]" data-testid="batch-digest-controls">
          <div className="flex items-center gap-2 mb-2">
            <Mail size={14} className="text-[#B8860B]" />
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#9A9A90] font-bold">Weekly Digest (Thursday recap)</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => patchSettings({ digest_enabled: !settings?.digest_enabled })}
              data-testid="batch-digest-toggle"
              className={`flex items-center gap-1.5 text-[11px] rounded-md px-2.5 py-2 border transition-colors ${settings?.digest_enabled ? "border-[#C9A227] bg-[#C9A227]/15 text-[#E4C97A] font-semibold" : "border-[#2A2A22] text-[#8A8A80]"}`}>
              <CircleDot size={13} /> {settings?.digest_enabled ? "Enabled" : "Disabled"}
            </button>
            <input type="email" defaultValue={settings?.digest_email || ""} placeholder="recap recipient email"
              onBlur={(e) => { if ((e.target.value || "") !== (settings?.digest_email || "")) patchSettings({ digest_email: e.target.value }); }}
              data-testid="batch-digest-email"
              className="flex-1 min-w-[200px] bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" />
            <button onClick={sendDigestNow} disabled={sendingDigest}
              data-testid="batch-digest-send-btn"
              className="flex items-center gap-1.5 text-[11px] font-semibold border border-[#6C8CD5] text-[#9db4e0] hover:bg-[#6C8CD5]/10 rounded-md px-3 py-2 disabled:opacity-50">
              {sendingDigest ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email digest now
            </button>
          </div>
          <div className="text-[10px] text-[#6A6A60] mt-1.5">Every Thursday, a recap of the week's batches (pool size, delivery, accept/decline tally) is emailed to the recipient above.</div>
        </div>
      </div>

      {/* ── Discounting banks rolodex ── */}
      <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5 mb-6" data-testid="batch-banks-rolodex">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Building2 size={15} className="text-[#B8860B]" />
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#9A9A90] font-bold">Discounting Banks Rolodex</div>
          <span className="text-[10px] text-[#6A6A60]">{banks.length} on file · {banks.filter((x) => !x.fax_number).length} fax pending</span>
          <button onClick={seedBanks} disabled={seeding}
            className="ml-auto flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border border-[#B8860B] text-[#E4C97A] hover:bg-[#B8860B]/10 rounded-md px-2.5 py-1.5 disabled:opacity-50"
            data-testid="batch-seed-banks-btn">
            {seeding ? <Loader2 size={12} className="animate-spin" /> : <Landmark size={12} />} Seed major banks
          </button>
          <button onClick={seedVerifiedBanks} disabled={seedingVerified}
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border border-[#3DBB8E] text-[#7fe7c2] hover:bg-[#3DBB8E]/10 rounded-md px-2.5 py-1.5 disabled:opacity-50"
            data-testid="batch-seed-verified-btn">
            {seedingVerified ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Load verified banks
          </button>
          <button onClick={bulkVerify} disabled={verifying}
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border border-[#3DBB8E] text-[#7fe7c2] hover:bg-[#3DBB8E]/10 rounded-md px-2.5 py-1.5 disabled:opacity-50"
            data-testid="batch-bulk-verify-btn">
            {verifying ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {verifying && verifyProgress ? `Verifying ${verifyProgress.done}/${verifyProgress.total}` : "Bulk verify (AI/FDIC)"}
          </button>
        </div>
        <div className="text-[10px] text-[#7A7A70] mb-3 -mt-1">
          Seeding loads ~50 real US &amp; international trade-finance banks <b>with fax left blank</b> — I never invent fax numbers. Use “Bulk verify”, “Look up”, or enter the verified fax, then save.
        </div>

        {banks.length > 0 && (() => {
          const verified = banks.filter((x) => (x.fax_number || "").trim()).length;
          const pct = Math.round((verified / banks.length) * 100);
          return (
            <div className="mb-4 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5" data-testid="batch-coverage-meter">
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <span className="text-[#9A9A90] font-medium">Fax verification coverage</span>
                <span className={verified === banks.length ? "text-[#3DBB8E] font-semibold" : "text-[#E4C97A] font-semibold"}>
                  {verified} of {banks.length} banks have a verified fax ({pct}%)
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-[#22221A] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, background: pct === 100 ? "#3DBB8E" : "linear-gradient(90deg,#B8860B,#E4C97A)" }} />
              </div>
              <div className="text-[10px] text-[#6A6A60] mt-1.5">
                {verified === 0 ? "No banks are eFax-ready yet — verify a fax to enable hands-off Wednesday runs."
                  : verified === banks.length ? "All banks are eFax-ready for hands-off Wednesday runs."
                  : `${banks.length - verified} still fax-pending and can't be auto-eFaxed until verified.`}
              </div>
            </div>
          );
        })()}

        {suggestions.length > 0 && (
          <div className="mb-4 border border-[#3DBB8E]/30 bg-[#0d2018] rounded-lg p-3" data-testid="batch-suggestions-review">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#7fe7c2] font-bold mb-2 flex items-center gap-1.5">
              <Sparkles size={13} /> Suggestions to review ({suggestions.length})
            </div>
            <div className="text-[10px] text-[#8A8A80] mb-2">AI/FDIC found the below — SWIFT/address are public &amp; verifiable; fax stays <b>unverified</b>. Approve to write onto the bank, or dismiss.</div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {suggestions.map((b) => {
                const s = b.fax_suggestion || {};
                return (
                  <div key={b.id} className="flex items-center justify-between gap-3 bg-[#0F1A14] border border-[#2A2A22] rounded-md px-3 py-2" data-testid={`batch-suggestion-${b.id}`}>
                    <div className="min-w-0">
                      <div className="text-[12px] text-[#ECECE4] font-medium truncate">{b.name}</div>
                      <div className="text-[10px] text-[#8A8A80] font-mono">
                        {s.fax_number ? `fax ${s.fax_number}` : "fax —"}{s.swift ? ` · SWIFT ${s.swift}` : ""}{s.aba ? ` · ABA ${s.aba}` : ""}{s.address ? ` · ${s.address}` : ""}
                        <span className={`ml-1 ${s.confidence === "verified" ? "text-[#3DBB8E]" : "text-[#B8860B]"}`}>({s.confidence || "unverified"})</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => applySuggestion(b.id, true)}
                        className="flex items-center gap-1 text-[10px] rounded-md px-2 py-1 border border-[#3DBB8E] text-[#7fe7c2] hover:bg-[#3DBB8E]/15"
                        data-testid={`batch-suggestion-accept-${b.id}`}><Check size={11} /> Approve</button>
                      <button onClick={() => applySuggestion(b.id, false)}
                        className="flex items-center gap-1 text-[10px] rounded-md px-2 py-1 border border-[#2A2A22] text-[#8A8A80] hover:text-red-400 hover:border-red-500/40"
                        data-testid={`batch-suggestion-reject-${b.id}`}><X size={11} /> Dismiss</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {banks.length > 6 && (
          <input value={bankFilter} onChange={(e) => setBankFilter(e.target.value)} placeholder="Filter banks…"
            className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4] mb-3" data-testid="batch-bank-filter" />
        )}
        {banks.length === 0 ? (
          <div className="text-[12px] text-[#5C5C5C] mb-4">No banks yet — “Seed major banks”, import a CSV, or add one below.</div>
        ) : (
          <div className="space-y-2 mb-4 max-h-[360px] overflow-y-auto pr-1">
            {banks.filter((b) => !bankFilter.trim() || `${b.name} ${b.city} ${b.state} ${b.country} ${b.email} ${b.swift}`.toLowerCase().includes(bankFilter.toLowerCase())).map((b) => (
              <React.Fragment key={b.id}>
              <div className="flex items-center justify-between gap-3 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2" data-testid={`batch-bank-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] text-[#ECECE4] font-medium truncate flex items-center gap-2">
                    {b.name}
                    {b.fax_confirmed_at && <span className="text-[9px] uppercase tracking-[0.1em] text-[#3DBB8E] border border-[#3DBB8E]/40 rounded px-1.5 py-0.5 shrink-0" data-testid={`batch-bank-fax-verified-${b.id}`}>eFax verified</span>}
                    {b.fax_web_verified && !b.fax_confirmed_at && <span className="text-[9px] uppercase tracking-[0.1em] text-[#6C8CD5] border border-[#6C8CD5]/40 rounded px-1.5 py-0.5 shrink-0" title={b.fax_source || "Found on the bank's official site"} data-testid={`batch-bank-fax-web-${b.id}`}>fax · web-sourced</span>}
                    {!b.fax_number && <span className="text-[9px] uppercase tracking-[0.1em] text-[#B8860B] border border-[#B8860B]/40 rounded px-1.5 py-0.5 shrink-0" data-testid={`batch-bank-pending-${b.id}`}>fax pending</span>}
                    {b.accreditation?.signed
                      ? <span className="text-[9px] uppercase tracking-[0.1em] text-[#3DBB8E] border border-[#3DBB8E]/40 rounded px-1.5 py-0.5 shrink-0" data-testid={`batch-bank-accredited-${b.id}`}>accredited{b.accreditation.exemption ? ` · ${b.accreditation.exemption}` : ""}</span>
                      : <span className="text-[9px] uppercase tracking-[0.1em] text-[#8A5A5A] border border-[#8A5A5A]/40 rounded px-1.5 py-0.5 shrink-0" data-testid={`batch-bank-unaccredited-${b.id}`}>not accredited</span>}
                  </div>
                  <div className="text-[11px] text-[#8A8A80] font-mono">{b.fax_number ? `eFax ${b.fax_number}` : "no fax on file"}{(b.country || b.city) ? ` · ${[b.city, b.state, b.country].filter(Boolean).join(", ")}` : ""}{b.aba ? ` · ABA ${b.aba}` : ""}{b.swift ? ` · ${b.swift}` : ""}</div>
                  {(b.email || b.phone || b.contact) && (
                    <div className="text-[10px] text-[#7A7A70] truncate" data-testid={`batch-bank-contact-${b.id}`}>{[b.contact, b.email, b.phone].filter(Boolean).join(" · ")}</div>
                  )}
                  {(b.instrument || b.lc_status || b.recourse || b.tenor) && (
                    <div className="text-[10px] text-[#6A7A6A] truncate" data-testid={`batch-bank-terms-${b.id}`}>{[b.instrument, b.lc_status, b.tenor, b.recourse].filter(Boolean).join(" · ")}</div>
                  )}
                  {b.last_fax?.status && (() => {
                    const st = String(b.last_fax.status).toLowerCase();
                    const ok = st === "delivered";
                    const bad = st.includes("fail") || st === "expired";
                    const col = ok ? "text-[#3DBB8E]" : bad ? "text-[#C87C7C]" : "text-[#B8860B]";
                    const when = b.last_fax.at ? new Date(b.last_fax.at).toLocaleString() : "";
                    return (
                      <div className={`text-[10px] ${col} truncate`} data-testid={`batch-bank-lastfax-${b.id}`}>
                        Last fax: {ok ? "delivered ✓" : bad ? `${b.last_fax.status}${b.last_fax.failure_reason ? ` (${b.last_fax.failure_reason})` : ""}` : b.last_fax.status}
                        {when ? ` · ${when}` : ""}{b.last_fax.batch_no ? ` · ${b.last_fax.batch_no}` : ""}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setAccBank(accBank === b.id ? null : b.id)}
                    className="flex items-center gap-1 text-[11px] text-[#E4C97A] hover:underline" data-testid={`batch-bank-accredit-btn-${b.id}`}>
                    <ShieldCheck size={12} /> Accredit
                  </button>
                  <button onClick={() => lookupFax(b)} disabled={lookupId === b.id}
                    className="flex items-center gap-1 text-[11px] text-[#7fe7c2] hover:underline disabled:opacity-50" data-testid={`batch-bank-lookup-${b.id}`}>
                    {lookupId === b.id ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Look up
                  </button>
                  <button onClick={() => { setBankForm({ name: b.name, fax_number: b.fax_number || "", contact: b.contact || "", city: b.city || "", state: b.state || "", country: b.country || "", email: b.email || "", phone: b.phone || "", aba: b.aba || "", swift: b.swift || "", instrument: b.instrument || "", lc_status: b.lc_status || "", tenor: b.tenor || "", recourse: b.recourse || "", notes: b.notes || "" }); setEditingBankId(b.id); }}
                    className="text-[11px] text-[#6C8CD5] hover:underline" data-testid={`batch-bank-edit-${b.id}`}>Edit</button>
                  <button onClick={() => deleteBank(b.id)} className="text-[#8A5A5A] hover:text-red-400" data-testid={`batch-bank-delete-${b.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
              {accBank === b.id && (
                <AccreditationForm bank={b} onSave={saveAccreditation} onCancel={() => setAccBank(null)} />
              )}
              </React.Fragment>
            ))}
          </div>
        )}
        <div className="grid md:grid-cols-4 gap-2" data-testid="batch-bank-form">
          <input placeholder="Bank name *" value={bankForm.name} onChange={(e) => setBankForm({ ...bankForm, name: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4] md:col-span-2" data-testid="batch-bank-name-input" />
          <input placeholder="eFax (+1…) — verify before sending" value={bankForm.fax_number} onChange={(e) => setBankForm({ ...bankForm, fax_number: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" data-testid="batch-bank-fax-input" />
          <input placeholder="Contact / desk" value={bankForm.contact} onChange={(e) => setBankForm({ ...bankForm, contact: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" />
          <input placeholder="City" value={bankForm.city} onChange={(e) => setBankForm({ ...bankForm, city: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" />
          <input placeholder="State" value={bankForm.state} onChange={(e) => setBankForm({ ...bankForm, state: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" />
          <input placeholder="ABA routing" value={bankForm.aba} onChange={(e) => setBankForm({ ...bankForm, aba: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" />
          <input placeholder="SWIFT" value={bankForm.swift} onChange={(e) => setBankForm({ ...bankForm, swift: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" />
          <input placeholder="Country" value={bankForm.country} onChange={(e) => setBankForm({ ...bankForm, country: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" data-testid="batch-bank-country-input" />
          <input placeholder="Verified email" value={bankForm.email} onChange={(e) => setBankForm({ ...bankForm, email: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" data-testid="batch-bank-email-input" />
          <input placeholder="Phone" value={bankForm.phone} onChange={(e) => setBankForm({ ...bankForm, phone: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" data-testid="batch-bank-phone-input" />
          <input placeholder="Instrument / facility" value={bankForm.instrument} onChange={(e) => setBankForm({ ...bankForm, instrument: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4] md:col-span-2" data-testid="batch-bank-instrument-input" />
          <input placeholder="LC / Non-LC status" value={bankForm.lc_status} onChange={(e) => setBankForm({ ...bankForm, lc_status: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" data-testid="batch-bank-lc-input" />
          <input placeholder="Recourse" value={bankForm.recourse} onChange={(e) => setBankForm({ ...bankForm, recourse: e.target.value })}
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4]" data-testid="batch-bank-recourse-input" />
        </div>
        <div className="flex gap-2 mt-2">
          <button onClick={saveBank} disabled={savingBank}
            className="flex items-center gap-1.5 text-[12px] font-semibold bg-[#B8860B] hover:bg-[#d19f22] text-[#0A0A0A] rounded-md px-3 py-2 disabled:opacity-50" data-testid="batch-bank-save-btn">
            {savingBank ? <Loader2 size={13} className="animate-spin" /> : editingBankId ? <Save size={13} /> : <Plus size={13} />}
            {editingBankId ? "Update bank" : "Add bank"}
          </button>
          {editingBankId && (
            <button onClick={() => { setBankForm(EMPTY_BANK); setEditingBankId(null); }}
              className="text-[12px] text-[#8A8A80] hover:text-[#ECECE4] px-3 py-2">Cancel</button>
          )}
        </div>

        {/* Bulk CSV import */}
        <div className="mt-5 pt-4 border-t border-[#2A2A22]" data-testid="batch-bank-import">
          <div className="flex items-center gap-2 mb-2">
            <Upload size={14} className="text-[#B8860B]" />
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#9A9A90] font-bold">Bulk import (CSV)</div>
          </div>
          <div className="text-[10px] text-[#6A6A60] mb-2 font-mono">
            Columns: name, fax_number, contact, city, state, aba, swift, notes · a header row is optional.
          </div>
          <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={4}
            placeholder={"name,fax_number,city,state,aba,swift\nFirst Discount Bank,+13125550123,Chicago,IL,071000013,FDBKUS44"}
            className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[11px] font-mono text-[#ECECE4]"
            data-testid="batch-csv-textarea" />
          <button onClick={importBanks} disabled={importing}
            className="mt-2 flex items-center gap-1.5 text-[12px] font-semibold border border-[#B8860B] text-[#E4C97A] hover:bg-[#B8860B]/10 rounded-md px-3 py-2 disabled:opacity-50"
            data-testid="batch-csv-import-btn">
            {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Import banks
          </button>
        </div>
      </div>

      {/* ── Treasury Forms signatories ── */}
      <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5 mb-6" data-testid="batch-treasury-forms-settings">
        <div className="flex items-center gap-2 flex-wrap">
          <Landmark size={15} className="text-[#B8860B]" />
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#9A9A90] font-bold">Treasury Forms — Signatories &amp; Filing</div>
          <button onClick={() => setFormsOpen((v) => !v)} data-testid="batch-forms-settings-toggle"
            className="ml-auto flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border border-[#B8860B] text-[#E4C97A] hover:bg-[#B8860B]/10 rounded-md px-2.5 py-1.5">
            {formsOpen ? "Close" : "Edit signatories"}
          </button>
        </div>
        <div className="text-[10px] text-[#7A7A70] mt-1.5">
          These names/EIN auto-fill FS Form 1010, the TCMM 31 CFR 225 pledge, and the redemption coupon.
          {formsSettings && <span className="text-[#8A8A80]"> Current officer: <b className="text-[#ECECE4]">{formsSettings.officer_name}</b> · {formsSettings.officer_title}</span>}
        </div>
        {formsOpen && formsSettings && (
          <div className="grid md:grid-cols-2 gap-2 mt-3" data-testid="batch-forms-settings-form">
            {[["organization_name","Organization name"],["ein","EIN"],["officer_name","Authorized officer name"],["officer_title","Officer title"],["secretary_name","Certifying secretary name"],["secretary_title","Secretary title"],["organization_address","Organization address"]].map(([k,ph]) => (
              <input key={k} value={formsSettings[k] || ""} placeholder={ph}
                onChange={(e) => setFormsSettings((s) => ({ ...s, [k]: e.target.value }))}
                data-testid={`batch-forms-${k}`}
                className={`bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2.5 py-2 text-[12px] text-[#ECECE4] ${k === "organization_address" ? "md:col-span-2" : ""}`} />
            ))}
            <div className="md:col-span-2">
              <button onClick={saveFormsSettings} disabled={savingForms} data-testid="batch-forms-settings-save"
                className="flex items-center gap-1.5 text-[12px] font-semibold bg-[#B8860B] hover:bg-[#d19f22] text-[#0A0A0A] rounded-md px-3 py-2 disabled:opacity-50">
                {savingForms ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save signatories
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Eligible loans ── */}
      <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5 mb-6" data-testid="batch-eligible-loans">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#9A9A90] font-bold">
            Ready for Outright Sale — flag the loans to include
          </div>
          <div className="flex items-center gap-2">
          {loans.some((l) => l.batch_sold) && (
            <button onClick={returnAllSold} data-testid="batch-return-all-sold"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm border border-[#5A2A2A] text-[#C87C7C] hover:bg-[#C05555]/10 px-3 py-1.5">
              <RefreshCw size={12} /> Return all sold to Ready
            </button>
          )}
          {loans.some((l) => !l.ready_for_sale && !l.batch_sold) && (
            <button onClick={flagAllReady} data-testid="batch-flag-all-ready"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm border border-[#3DBB8E]/60 text-[#7fe7c2] hover:bg-[#3DBB8E]/10 px-3 py-1.5">
              <CheckCircle2 size={12} /> Flag all Ready
            </button>
          )}
          {loans.some((l) => l.ready_for_sale && !l.batch_sold && !excluded.has(l.id)) && (
            <button onClick={() => setBulkTreasuryOpen((v) => !v)} data-testid="batch-bulk-treasury-btn"
              title="Attach the same Treasury Repo data to every included (Ready) borrower at once."
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm border px-3 py-1.5 ${bulkTreasuryOpen ? "border-[#B8860B] text-[#E4C97A] bg-[#B8860B]/10" : "border-[#B8860B]/60 text-[#E4C97A] hover:bg-[#B8860B]/10"}`}>
              <Landmark size={12} /> {bulkTreasuryOpen ? "Close bulk Treasury" : "Bulk attach Treasury"}
            </button>
          )}
          {loans.some((l) => l.ready_for_sale && !l.batch_sold && !excluded.has(l.id)) && (
            <button onClick={sendTestToTcmm} disabled={testingTcmm} data-testid="batch-test-tcmm-btn"
              title="Fire a safe live end-to-end test to the Treasury Collateral (TCMM) desk — package + Treasury forms + a counterparty standstill, all to TCMM. No real bank/lender contacted; loan is not marked sold."
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm border border-[#6C8CD5]/60 text-[#9DB4E8] hover:bg-[#6C8CD5]/10 px-3 py-1.5 disabled:opacity-50">
              {testingTcmm ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send test to TCMM
            </button>
          )}
          </div>
        </div>
        {bulkTreasuryOpen && (
          <div className="mb-3" data-testid="batch-bulk-treasury-panel">
            <TreasuryAttachForm
              loan={{ id: "bulk", borrower_name: `${loans.filter((l) => l.ready_for_sale && !l.batch_sold && !excluded.has(l.id)).length} included borrower(s)`, treasury_settings: null }}
              onSave={(_loan, settings) => saveBulkTreasury(settings)}
              saving={savingBulkTreasury}
              onCancel={() => setBulkTreasuryOpen(false)}
              loadAuctions={loadAuctions}
            />
          </div>
        )}
        {loans.length === 0 ? (
          <div className="text-[12px] text-[#5C5C5C]">No approved loans available.</div>
        ) : (
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
            {loans.map((l) => (
              <React.Fragment key={l.id}>
              <div className={`flex items-center justify-between gap-3 rounded-md px-3 py-2 border ${l.batch_sold ? "border-[#2A2A22] bg-[#0d0d09] opacity-60" : l.ready_for_sale ? "border-[#3DBB8E]/40 bg-[#0d2018]" : "border-[#2A2A22] bg-[#0F0F0A]"}`} data-testid={`batch-loan-row-${l.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {l.ready_for_sale && !l.batch_sold && (
                    <input type="checkbox" checked={!excluded.has(l.id)} onChange={() => toggleExcluded(l.id)} data-testid={`batch-include-${l.id}`} title="Include this borrower in the next build" />
                  )}
                  <div className="min-w-0">
                    <div className="text-[13px] text-[#ECECE4] font-medium truncate flex items-center gap-2">
                      {l.borrower_name} <span className="text-[#8A8A80] font-normal">· {fmtUSD(l.face_value)}</span>
                      {Number(l.face_value || 0) > LARGE_FACE && (
                        <span className="text-[9px] uppercase tracking-[0.1em] text-[#E4B84A] border border-[#E4B84A]/50 bg-[#E4B84A]/10 rounded px-1.5 py-0.5 shrink-0 inline-flex items-center gap-1" title="Unusually large face value — verify this amount is correct before assembling." data-testid={`batch-loan-largeface-${l.id}`}>
                          <AlertTriangle size={9} /> verify amount
                        </span>
                      )}
                      {l.has_treasury
                        ? <span className="text-[9px] uppercase tracking-[0.1em] text-[#7fe7c2] border border-[#3DBB8E]/40 rounded px-1.5 py-0.5 shrink-0" data-testid={`batch-loan-treasury-on-${l.id}`}>Treasury ✓</span>
                        : <span className="text-[9px] uppercase tracking-[0.1em] text-[#8A8A80] border border-[#2A2A22] rounded px-1.5 py-0.5 shrink-0" data-testid={`batch-loan-treasury-off-${l.id}`}>No Treasury</span>}
                    </div>
                    <div className="text-[11px] text-[#8A8A80]">Counterparty: {l.counterparty || "—"}{l.batch_sold ? " · already sold in a batch" : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!l.batch_sold && l.has_treasury && (
                    <>
                      <button onClick={() => downloadTreasuryForms(l)} data-testid={`batch-loan-forms-dl-${l.id}`}
                        title="Open the auto-filled Treasury forms packet: FS Form 1010, TCMM 31 CFR 225 Collateral Pledge, and the Treasury Security Redemption Coupon."
                        className="flex items-center gap-1.5 text-[11px] rounded-md px-2.5 py-1.5 border border-[#2A2A22] text-[#9db4e0] hover:border-[#6C8CD5] transition-colors">
                        <FileDown size={13} /> Forms
                      </button>
                      <button onClick={() => efaxTreasuryForms(l)} data-testid={`batch-loan-forms-efax-${l.id}`}
                        title="eFax the Treasury forms packet to the counterparty or the Treasury Collateral (TCMM) desk."
                        className="flex items-center gap-1.5 text-[11px] rounded-md px-2.5 py-1.5 border border-[#2A2A22] text-[#7fe7c2] hover:border-[#3DBB8E] transition-colors">
                        <Send size={13} /> eFax Forms
                      </button>
                    </>
                  )}
                  {!l.batch_sold && (
                    <button onClick={() => openTreasury(l)} data-testid={`batch-loan-treasury-btn-${l.id}`}
                      title="Attach Treasury Repo & Collateral Financing data so the full Treasury package is bundled into the batch for this borrower."
                      className={`flex items-center gap-1.5 text-[11px] rounded-md px-2.5 py-1.5 border transition-colors ${treasuryEdit === l.id ? "border-[#B8860B] text-[#E4C97A] bg-[#B8860B]/10" : l.has_treasury ? "border-[#3DBB8E]/50 text-[#7fe7c2]" : "border-[#2A2A22] text-[#9DB4E8] hover:border-[#6C8CD5]"}`}>
                      <Landmark size={13} /> {treasuryEdit === l.id ? "Close" : (l.has_treasury ? "Edit Treasury" : "Attach Treasury")}
                    </button>
                  )}
                  {l.batch_sold ? (
                    <button onClick={() => unsellLoan(l)}
                      data-testid={`batch-loan-unsell-${l.id}`}
                      title="Fax failed? Return this loan to the Ready pool so you can re-batch and re-send it."
                      className="flex items-center gap-1.5 text-[11px] rounded-md px-2.5 py-1.5 border border-[#5A2A2A] text-[#C87C7C] hover:bg-[#C05555]/10 transition-colors">
                      <RefreshCw size={13} /> Return to Ready
                    </button>
                  ) : (
                    <button onClick={() => toggleReady(l.id, !l.ready_for_sale)}
                      data-testid={`batch-ready-toggle-${l.id}`}
                      className={`flex items-center gap-1.5 text-[11px] rounded-md px-2.5 py-1.5 border transition-colors ${l.ready_for_sale ? "border-[#3DBB8E] bg-[#3DBB8E]/15 text-[#7fe7c2] font-semibold" : "border-[#2A2A22] text-[#8A8A80] hover:border-[#3DBB8E]/50"}`}>
                      {l.ready_for_sale ? <CheckCircle2 size={13} /> : <CircleDot size={13} />}
                      {l.ready_for_sale ? "Ready" : "Add to batch"}
                    </button>
                  )}
                </div>
              </div>
              {treasuryEdit === l.id && (
                <TreasuryAttachForm loan={l} onSave={saveTreasury} saving={savingTreasury === l.id} onCancel={() => setTreasuryEdit(null)} loadAuctions={loadAuctions} />
              )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* ── Assembled packages ── */}
      <div data-testid="batch-packages-list">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#9A9A90] font-bold">Assembled Batch Packages</div>
          <button onClick={() => setFailedOnly((v) => !v)} data-testid="batch-failed-only-toggle"
            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm border px-3 py-1.5 ${failedOnly ? "border-[#C05555] text-[#E39898] bg-[#C05555]/10" : "border-[#2A2A22] text-[#8A8A80] hover:border-[#C05555]/50"}`}>
            <AlertTriangle size={12} /> {failedOnly ? "Showing failed/undelivered" : "Show failed only"}
          </button>
        </div>
        {packages.length > 0 && (
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="batch-bulk-bar">
            <span className="text-[11px] text-[#8A8A80]">Bulk re-send:</span>
            <button onClick={() => setSelectedPkgs(packages.map((p) => p.id))}
              className="text-[11px] border border-[#2A2A22] hover:border-[#6C8CD5] text-[#9db4e0] rounded-md px-2.5 py-1.5"
              data-testid="batch-bulk-select-all">Select all ({packages.length})</button>
            <button onClick={selectFailedPkgs}
              className="text-[11px] border border-[#5A2A2A] hover:border-[#C05555] text-[#C87C7C] rounded-md px-2.5 py-1.5"
              data-testid="batch-bulk-select-failed">Select failed only</button>
            {selectedPkgs.length > 0 && (
              <button onClick={() => setSelectedPkgs([])}
                className="text-[11px] border border-[#2A2A22] hover:border-[#8A8A80] text-[#8A8A80] rounded-md px-2.5 py-1.5"
                data-testid="batch-bulk-clear">Clear ({selectedPkgs.length})</button>
            )}
            <div className="flex-1 min-w-[200px]">
              <select value={bulkBankId} onChange={(e) => setBulkBankId(e.target.value)}
                className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[12px] text-[#ECECE4]"
                data-testid="batch-bulk-bank-select">
                <option value="">— pick a bank to re-send to —</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}{b.fax_number ? ` · ${b.fax_number}` : " · (no fax)"}{b.accreditation?.signed ? " · accredited" : " · not accredited"}
                  </option>
                ))}
              </select>
            </div>
            <button onClick={bulkResend} disabled={bulkSending || selectedPkgs.length === 0 || !bulkBankId}
              className="flex items-center gap-1.5 text-[12px] border border-[#C9A227] text-[#E4C97A] hover:bg-[#C9A227]/10 rounded-md px-4 py-2 disabled:opacity-40"
              data-testid="batch-bulk-send">
              {bulkSending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Re-send selected ({selectedPkgs.length})
            </button>
          </div>
        )}
        {packages.length === 0 ? (
          <div className="text-[12px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No batch packages yet.</div>
        ) : (
          <div className="space-y-2">
            {(() => {
              const isFailed = (p) => {
                const d = String(p.delivery_status || "").toLowerCase();
                const s = String(p.status || "").toLowerCase();
                return s === "efax_failed" || d === "failed" || d === "delivery_failed" || d === "expired";
              };
              const shown = failedOnly ? packages.filter(isFailed) : packages;
              if (shown.length === 0) return (
                <div className="text-[12px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="batch-no-failed">No failed or undelivered packages. 🎉</div>
              );
              return shown.map((p) => (
              <div key={p.id} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-4" data-testid={`batch-pkg-${p.id}`}>
               <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex items-start gap-3">
                  <input type="checkbox" checked={selectedPkgs.includes(p.id)} onChange={() => togglePkgSelect(p.id)}
                    className="mt-1 accent-[#C9A227] cursor-pointer" data-testid={`batch-pkg-select-${p.id}`} />
                  <div className="min-w-0">
                  <div className="text-[13px] text-[#ECECE4] font-semibold flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{p.batch_no}</span>
                    <StatusChip status={p.status} />
                    <DeliveryChip status={p.delivery_status} />
                    {p.auto && <span className="text-[9px] uppercase tracking-[0.12em] text-[#B8860B] border border-[#B8860B]/40 rounded px-1.5 py-0.5">Wednesday auto</span>}
                    {p.is_test && <span className="text-[9px] uppercase tracking-[0.12em] text-[#9DB4E8] border border-[#6C8CD5]/50 bg-[#6C8CD5]/10 rounded px-1.5 py-0.5" data-testid={`batch-pkg-test-${p.id}`}>TEST → TCMM</span>}
                  </div>
                  <div className="text-[11px] text-[#8A8A80] mt-1">
                    {p.pool_count} borrower{p.pool_count === 1 ? "" : "s"} · {fmtUSD(p.total_face)} face · → {p.accepting_bank?.name || "—"} ({p.accepting_bank?.fax_number || "—"})
                  </div>
                  <div className="text-[10px] text-[#5C5C5C] mt-0.5">
                    {new Date(p.created_at).toLocaleString()}{p.delivery_failure_reason ? ` · ${p.delivery_failure_reason}` : ""}
                    {(p.pool_count > 0) && ` · reconcile: ${p.accepted_count || 0} accepted / ${p.declined_count || 0} declined / ${p.pending_count ?? p.pool_count} pending`}
                  </div>
                  {(p.treasury_pkg_count > 0 || p.treasury_collateral_copy || (p.counterparty_standstills || []).length > 0) && (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid={`batch-pkg-integrations-${p.id}`}>
                      {p.treasury_pkg_count > 0 && (
                        <span className="text-[9px] uppercase tracking-[0.1em] text-[#7fe7c2] border border-[#3DBB8E]/40 rounded px-1.5 py-0.5" data-testid={`batch-pkg-treasury-count-${p.id}`}>
                          Treasury pkg × {p.treasury_pkg_count}
                        </span>
                      )}
                      {p.treasury_collateral_copy?.status && p.treasury_collateral_copy.status !== "skipped" && (
                        <span className={`text-[9px] uppercase tracking-[0.1em] rounded px-1.5 py-0.5 border ${p.treasury_collateral_copy.status === "failed" ? "text-[#C87C7C] border-[#8A5A5A]/50" : "text-[#9DB4E8] border-[#6C8CD5]/40"}`} data-testid={`batch-pkg-tcmm-${p.id}`}>
                          TCMM copy: {p.treasury_collateral_copy.status}
                        </span>
                      )}
                      {(p.counterparty_standstills || []).length > 0 && (() => {
                        const css = p.counterparty_standstills;
                        const sent = css.filter((x) => x.status && !["skipped", "failed"].includes(x.status)).length;
                        return (
                          <span className="text-[9px] uppercase tracking-[0.1em] text-[#E4C97A] border border-[#B8860B]/40 rounded px-1.5 py-0.5" data-testid={`batch-pkg-standstill-${p.id}`}>
                            Standstill → counterparty: {sent}/{css.length}
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setExpandedPkg(expandedPkg === p.id ? null : p.id)}
                    className="flex items-center gap-1.5 text-[11px] border border-[#2A2A22] hover:border-[#3DBB8E] text-[#7fe7c2] rounded-md px-3 py-1.5"
                    data-testid={`batch-pkg-reconcile-toggle-${p.id}`}>
                    <ClipboardList size={13} /> Reconcile
                  </button>
                  {p.package_url && (
                    <a href={p.package_url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 text-[11px] border border-[#2A2A22] hover:border-[#6C8CD5] text-[#9db4e0] rounded-md px-3 py-1.5"
                      data-testid={`batch-pkg-download-${p.id}`}>
                      <FileDown size={13} /> Print / Download
                    </a>
                  )}
                  <button onClick={() => efaxPackage(p)}
                    className="flex items-center gap-1.5 text-[11px] border border-[#C9A227] text-[#E4C97A] hover:bg-[#C9A227]/10 rounded-md px-3 py-1.5"
                    data-testid={`batch-pkg-efax-${p.id}`}>
                    <Send size={13} /> {p.status === "sent" ? "Re-send eFax" : "eFax to bank"}
                  </button>
                  <button onClick={() => { setResendPkg(resendPkg === p.id ? null : p.id); }}
                    className="flex items-center gap-1.5 text-[11px] border border-[#6C8CD5] text-[#9db4e0] hover:bg-[#6C8CD5]/10 rounded-md px-3 py-1.5"
                    data-testid={`batch-pkg-resend-toggle-${p.id}`}>
                    <Building2 size={13} /> Re-send to another bank
                  </button>
                  {p.fax_id && (
                    <button onClick={() => refreshFaxStatus(p)}
                      className="flex items-center gap-1.5 text-[11px] border border-[#3D6EBB] text-[#8fb4ec] hover:bg-[#3D6EBB]/10 rounded-md px-3 py-1.5"
                      data-testid={`batch-pkg-refresh-${p.id}`}>
                      <RefreshCw size={13} /> Refresh status
                    </button>
                  )}
                  <button onClick={() => deletePackage(p)}
                    className="flex items-center gap-1.5 text-[11px] border border-[#5A2A2A] text-[#C87C7C] hover:bg-[#C05555]/10 rounded-md px-3 py-1.5"
                    data-testid={`batch-pkg-delete-${p.id}`}>
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
               </div>
               {resendPkg === p.id && (
                 <div className="mt-3 pt-3 border-t border-[#2A2A22] flex items-end gap-2 flex-wrap" data-testid={`batch-pkg-resend-panel-${p.id}`}>
                   <div className="flex-1 min-w-[220px]">
                     <label className="block text-[10px] uppercase tracking-[0.14em] text-[#8A8A80] font-bold mb-1">Re-send this batch to another discounting bank</label>
                     <select
                       value={resendBankId[p.id] || ""}
                       onChange={(e) => setResendBankId((prev) => ({ ...prev, [p.id]: e.target.value }))}
                       className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[12px] text-[#ECECE4]"
                       data-testid={`batch-pkg-resend-select-${p.id}`}>
                       <option value="">— pick an accepting/discounting bank —</option>
                       {banks.filter((b) => b.id !== (p.accepting_bank?.id)).map((b) => (
                         <option key={b.id} value={b.id}>
                           {b.name}{b.fax_number ? ` · ${b.fax_number}` : " · (no fax)"}{b.accreditation?.signed ? " · accredited" : " · not accredited"}
                         </option>
                       ))}
                     </select>
                   </div>
                   <button onClick={() => resendToBank(p)} disabled={resending === p.id || !resendBankId[p.id]}
                     className="flex items-center gap-1.5 text-[12px] border border-[#C9A227] text-[#E4C97A] hover:bg-[#C9A227]/10 rounded-md px-4 py-2 disabled:opacity-40"
                     data-testid={`batch-pkg-resend-send-${p.id}`}>
                     {resending === p.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Re-send now
                   </button>
                 </div>
               )}
               {expandedPkg === p.id && (
                 <div className="mt-3 pt-3 border-t border-[#2A2A22]" data-testid={`batch-pkg-reconcile-${p.id}`}>
                   <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                     <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A80] font-bold">
                       Bank accept / decline (from returned Acceptance Return Sheet)
                     </div>
                     <label className="flex items-center gap-1.5 text-[10px] cursor-pointer border border-[#3DBB8E]/50 text-[#7fe7c2] hover:bg-[#3DBB8E]/10 rounded-md px-2.5 py-1.5"
                       data-testid={`batch-ocr-upload-label-${p.id}`}>
                       {ocrPkg === p.id ? <Loader2 size={12} className="animate-spin" /> : <ScanLine size={12} />}
                       {ocrPkg === p.id ? "Reading…" : "Auto-reconcile from returned sheet"}
                       <input type="file" accept="application/pdf,image/*" className="hidden"
                         data-testid={`batch-ocr-upload-${p.id}`}
                         onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadReturnSheet(p.id, f); }} />
                     </label>
                   </div>
                   {p.auto_reconcile && (
                     <div className="text-[10px] text-[#8A8A80] mb-2" data-testid={`batch-auto-recon-note-${p.id}`}>
                       Auto-read via {p.auto_reconcile.source === "inbound_fax" ? "inbound fax" : "upload"} · applied {p.auto_reconcile.applied} · {new Date(p.auto_reconcile.at).toLocaleString()}
                     </div>
                   )}
                   <div className="space-y-1.5">
                     {(p.reconciliation || []).map((r) => (
                       <div key={r.app_id} className="flex items-center justify-between gap-3 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-1.5">
                         <div className="text-[12px] text-[#ECECE4] truncate">{r.borrower} <span className="text-[#8A8A80]">· {fmtUSD(r.face_value)}</span></div>
                         <div className="flex items-center gap-1.5 shrink-0">
                           {[["accepted", "Accepted", CheckCircle2, "#3DBB8E"], ["pending", "Pending", CircleDot, "#B8860B"], ["declined", "Declined", XCircle, "#C05555"]].map(([dec, lbl, Ic, col]) => (
                             <button key={dec} onClick={() => reconcile(p.id, r.app_id, dec)}
                               data-testid={`batch-recon-${dec}-${r.app_id}`}
                               className={`flex items-center gap-1 text-[10px] rounded-md px-2 py-1 border transition-colors ${r.decision === dec ? "font-semibold" : "opacity-60 hover:opacity-100"}`}
                               style={r.decision === dec ? { borderColor: col, color: col, background: `${col}22` } : { borderColor: "#2A2A22", color: "#8A8A80" }}>
                               <Ic size={11} /> {lbl}
                             </button>
                           ))}
                           <button onClick={() => removeLoanFromPkg(p, r)}
                             title="Remove this borrower from the batch and return the loan to the Ready pool"
                             data-testid={`batch-loan-remove-${r.app_id}`}
                             className="flex items-center gap-1 text-[10px] rounded-md px-2 py-1 border border-[#5A2A2A] text-[#C87C7C] hover:bg-[#C05555]/10 transition-colors">
                             <Trash2 size={11} /> Remove
                           </button>
                         </div>
                       </div>
                     ))}
                   </div>
                  {(p.counterparty_standstills || []).length > 0 && (
                    <div className="mt-4 pt-3 border-t border-[#2A2A22]" data-testid={`batch-standstill-panel-${p.id}`}>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A80] font-bold flex items-center gap-1.5 mb-2">
                        <Send size={12} /> Counterparty Standstill / Consolidation eFax
                      </div>
                      <div className="space-y-1.5">
                        {(p.counterparty_standstills || []).map((r) => {
                          const st = String(r.status || "").toLowerCase();
                          const ok = st && !["skipped", "failed"].includes(st);
                          const col = ok ? "#3DBB8E" : st === "failed" ? "#C05555" : "#B8860B";
                          const key = `${p.id}:${r.app_id}`;
                          const needsFax = st === "skipped" || st === "failed";
                          return (
                            <div key={r.app_id} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-1.5" data-testid={`batch-standstill-row-${r.app_id}`}>
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="text-[12px] text-[#ECECE4] truncate min-w-0">
                                  {r.borrower} <span className="text-[#8A8A80]">→ {r.counterparty || "—"}</span>
                                  {r.to && <span className="text-[10px] text-[#7A7A70] font-mono ml-1">{r.to}</span>}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-[10px] uppercase tracking-[0.1em] rounded px-1.5 py-0.5 border font-semibold"
                                    style={{ color: col, borderColor: `${col}66` }} data-testid={`batch-standstill-status-${r.app_id}`}>
                                    {st === "skipped" ? "skipped · no fax" : (r.status || "—")}
                                  </span>
                                  {needsFax && (
                                    <>
                                      <input placeholder="counterparty fax (+1…)" value={standstillResend[key] || ""}
                                        onChange={(e) => setStandstillResend((s) => ({ ...s, [key]: e.target.value }))}
                                        data-testid={`batch-standstill-fax-${r.app_id}`}
                                        className="w-40 bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-2 py-1 text-[11px] text-[#ECECE4]" />
                                      <button onClick={() => resendStandstill(p, r)} disabled={resendingStandstill === key}
                                        data-testid={`batch-standstill-resend-${r.app_id}`}
                                        className="flex items-center gap-1 text-[10px] rounded-md px-2 py-1 border border-[#B8860B] text-[#E4C97A] hover:bg-[#B8860B]/10 disabled:opacity-50">
                                        {resendingStandstill === key ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Re-send
                                      </button>
                                    </>
                                  )}
                                  {ok && (
                                    <button onClick={() => resendStandstill(p, r)} disabled={resendingStandstill === key}
                                      data-testid={`batch-standstill-resend-${r.app_id}`}
                                      className="flex items-center gap-1 text-[10px] rounded-md px-2 py-1 border border-[#2A2A22] text-[#8A8A80] hover:text-[#7fe7c2] hover:border-[#3DBB8E]/40 disabled:opacity-50">
                                      {resendingStandstill === key ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Re-send
                                    </button>
                                  )}
                                </div>
                              </div>
                              {r.reason && <div className="text-[10px] text-[#C87C7C] mt-0.5">{r.reason}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                   {(p.reconciliation || []).some((r) => r.decision === "accepted") && (
                     <div className="mt-4 pt-3 border-t border-[#2A2A22]" data-testid={`batch-remit-panel-${p.id}`}>
                       <div className="flex items-center justify-between mb-2">
                         <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A80] font-bold flex items-center gap-1.5">
                           <Banknote size={12} /> Counterparty remittance (wire / certified check)
                         </div>
                         <RemitStatusChip status={p.remittance_status} />
                       </div>
                       <div className="space-y-1.5">
                         {(p.reconciliation || []).filter((r) => r.decision === "accepted").map((r) => (
                           <RemittanceRow key={r.app_id} pkgId={p.id} row={r}
                             existing={(p.remittances || []).find((x) => x.app_id === r.app_id)} onSave={remit} onUploadProof={uploadProof} />
                         ))}
                       </div>
                     </div>
                   )}
                 </div>
               )}
              </div>
            ));
            })()}
          </div>
        )}
      </div>
    </section>
  );
}

function DeliveryChip({ status }) {
  if (!status) return null;
  const s = String(status).toLowerCase();
  const ok = ["delivered"].includes(s);
  const bad = ["failed", "delivery_failed"].includes(s);
  const c = ok ? "text-[#22c55e] border-[#22c55e]/40 bg-[#22c55e]/10"
    : bad ? "text-red-400 border-red-500/40 bg-red-500/10"
    : "text-[#B8860B] border-[#B8860B]/40 bg-[#B8860B]/10";
  const label = ok ? "Delivered ✓" : bad ? "Delivery failed" : `Fax: ${s}`;
  return (
    <span className={`text-[9px] uppercase tracking-[0.12em] border rounded px-1.5 py-0.5 ${c}`} title="Telnyx delivery receipt">
      {label}
    </span>
  );
}

function StatusChip({ status }) {
  const map = {
    sent: { c: "text-[#22c55e] border-[#22c55e]/40 bg-[#22c55e]/10", t: "Sent ✓" },
    draft: { c: "text-[#6C8CD5] border-[#6C8CD5]/40 bg-[#6C8CD5]/10", t: "Assembled" },
    efax_failed: { c: "text-red-400 border-red-500/40 bg-red-500/10", t: "eFax failed" },
  };
  const m = map[status] || map.draft;
  return (
    <span className={`text-[9px] uppercase tracking-[0.12em] border rounded px-1.5 py-0.5 flex items-center gap-1 ${m.c}`}>
      {status === "efax_failed" && <AlertTriangle size={9} />}{m.t}
    </span>
  );
}

function RemitStatusChip({ status }) {
  const map = {
    complete: { c: "text-[#22c55e] border-[#22c55e]/40 bg-[#22c55e]/10", t: "Remitted ✓" },
    partial: { c: "text-[#6C8CD5] border-[#6C8CD5]/40 bg-[#6C8CD5]/10", t: "In progress" },
    pending: { c: "text-[#B8860B] border-[#B8860B]/40 bg-[#B8860B]/10", t: "Not started" },
  };
  const m = map[status] || map.pending;
  return <span className={`text-[9px] uppercase tracking-[0.12em] border rounded px-1.5 py-0.5 ${m.c}`} data-testid="batch-remit-status-chip">{m.t}</span>;
}

function RemittanceRow({ pkgId, row, existing, onSave, onUploadProof }) {
  const [method, setMethod] = useState(existing?.method || "wire");
  const [reference, setReference] = useState(existing?.reference || "");
  const status = existing?.status || "pending";
  const post = (st) => onSave(pkgId, { app_id: row.app_id, method, reference, status: st, amount: row.face_value });
  const stCol = status === "confirmed" ? "#3DBB8E" : status === "sent" ? "#6C8CD5" : "#B8860B";
  return (
    <div className="bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2" data-testid={`batch-remit-row-${row.app_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
        <div className="text-[12px] text-[#ECECE4] truncate">{row.borrower} <span className="text-[#8A8A80]">· {fmtUSD(row.face_value)}</span></div>
        <span className="text-[9px] uppercase tracking-[0.12em] border rounded px-1.5 py-0.5" style={{ borderColor: stCol, color: stCol, background: `${stCol}22` }} data-testid={`batch-remit-rowstatus-${row.app_id}`}>{status}</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={method} onChange={(e) => setMethod(e.target.value)}
          className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1 text-[11px] text-[#ECECE4]" data-testid={`batch-remit-method-${row.app_id}`}>
          <option value="wire">Fedwire</option>
          <option value="check">Certified check</option>
        </select>
        <input value={reference} onChange={(e) => setReference(e.target.value)}
          placeholder={method === "check" ? "Check no." : "Wire ref."}
          className="flex-1 min-w-[120px] bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1 text-[11px] text-[#ECECE4]" data-testid={`batch-remit-ref-${row.app_id}`} />
        <button onClick={() => post("sent")} className="text-[10px] rounded-md px-2 py-1 border border-[#6C8CD5]/50 text-[#6C8CD5] hover:bg-[#6C8CD5]/10" data-testid={`batch-remit-sent-${row.app_id}`}>Mark sent</button>
        <button onClick={() => post("confirmed")} className="text-[10px] rounded-md px-2 py-1 border border-[#3DBB8E]/50 text-[#7fe7c2] hover:bg-[#3DBB8E]/10" data-testid={`batch-remit-confirmed-${row.app_id}`}>Confirmed</button>
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <label className="flex items-center gap-1 text-[10px] text-[#7fe7c2] cursor-pointer hover:underline" data-testid={`batch-remit-proof-label-${row.app_id}`}>
          <Upload size={11} /> {existing?.proof_url ? "Replace payoff confirmation" : "Attach payoff confirmation"}
          <input type="file" accept="application/pdf,image/*" className="hidden"
            data-testid={`batch-remit-proof-${row.app_id}`}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onUploadProof(pkgId, row.app_id, f); }} />
        </label>
        {existing?.proof_url && (
          <a href={existing.proof_url} target="_blank" rel="noreferrer"
            className="text-[10px] text-[#6C8CD5] hover:underline" data-testid={`batch-remit-proof-view-${row.app_id}`}>
            View: {existing.proof_name || "confirmation"}
          </a>
        )}
      </div>
    </div>
  );
}

function TreasuryAttachForm({ loan, onSave, saving, onCancel, loadAuctions }) {
  const t = loan.treasury_settings || {};
  const [f, setF] = useState({
    cusip: t.cusip || "", txnRef: t.txnRef || "", repoAmount: t.repoAmount || "",
    securityType: t.securityType || "", securityTerm: t.securityTerm || "",
    repoCounterparty: t.repoCounterparty || "", fiscalAgent: t.fiscalAgent || "",
    maturityDate: t.maturityDate || "", interestRate: t.interestRate || "", highYield: t.highYield || "",
  });
  const [auctions, setAuctions] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingAuctions, setLoadingAuctions] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inputCls = "bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1.5 text-[11px] text-[#ECECE4]";

  const togglePicker = async () => {
    if (pickerOpen) { setPickerOpen(false); return; }
    setPickerOpen(true);
    if (auctions.length === 0 && loadAuctions) {
      setLoadingAuctions(true);
      try {
        const rows = await loadAuctions();
        setAuctions(rows || []);
      } finally {
        setLoadingAuctions(false);
      }
    }
  };
  const pickSecurity = (s) => {
    setF((p) => ({ ...p, cusip: s.cusip || p.cusip, securityType: s.securityType || p.securityType, securityTerm: s.securityTerm || p.securityTerm,
      maturityDate: s.maturityDate || p.maturityDate, interestRate: s.interestRate || p.interestRate, highYield: s.highYield || p.highYield }));
    setPickerOpen(false);
  };

  return (
    <div className="bg-[#0d0d09] border border-[#B8860B]/30 rounded-md px-3 py-3 mb-1" data-testid={`batch-treasury-form-${loan.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#E4C97A] font-bold">Treasury Repo &amp; Collateral Financing — {loan.borrower_name}</div>
        <button onClick={togglePicker} data-testid={`batch-treasury-picker-btn-${loan.id}`}
          className="flex items-center gap-1.5 text-[10px] font-bold rounded-md px-2.5 py-1 border border-[#3DBB8E]/50 text-[#7fe7c2] hover:bg-[#3DBB8E]/10">
          {loadingAuctions ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />} {pickerOpen ? "Hide auctions" : "Pick from live auctions"}
        </button>
      </div>
      {pickerOpen && (
        <div className="mb-2 border border-[#2A2A22] rounded-md bg-[#0A0A0A] max-h-[180px] overflow-y-auto" data-testid={`batch-treasury-picker-${loan.id}`}>
          {loadingAuctions ? (
            <div className="text-[10px] text-[#8A8A80] px-3 py-3">Loading live US Treasury auctions…</div>
          ) : auctions.length === 0 ? (
            <div className="text-[10px] text-[#8A8A80] px-3 py-3">No auctions returned. Enter the CUSIP manually below.</div>
          ) : auctions.slice(0, 120).map((s) => (
            <button key={s.cusip} onClick={() => pickSecurity(s)} data-testid={`batch-treasury-auction-${s.cusip}`}
              className="w-full text-left px-3 py-1.5 border-b border-[#1a1a14] hover:bg-[#12120c]">
              <span className="text-[11px] text-[#ECECE4] font-mono">{s.cusip}</span>
              <span className="text-[10px] text-[#8A8A80] ml-2">{s.securityType} {s.securityTerm} · {s.interestRate ? `${s.interestRate}%` : (s.highYield ? `${s.highYield}% yld` : "")} · mat {s.maturityDate || "—"}</span>
            </button>
          ))}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-2 mb-2">
        <input value={f.cusip} onChange={(e) => set("cusip", e.target.value)} placeholder="CUSIP (e.g. 912796XY7)" className={inputCls} data-testid={`batch-treasury-cusip-${loan.id}`} />
        <input value={f.txnRef} onChange={(e) => set("txnRef", e.target.value)} placeholder="Transaction Reference" className={inputCls} data-testid={`batch-treasury-txnref-${loan.id}`} />
        <input value={f.repoAmount} onChange={(e) => set("repoAmount", e.target.value)} placeholder="Repo / financing amount" className={inputCls} data-testid={`batch-treasury-amount-${loan.id}`} />
        <input value={f.securityType} onChange={(e) => set("securityType", e.target.value)} placeholder="Security type (Bill / Note / Bond)" className={inputCls} data-testid={`batch-treasury-sectype-${loan.id}`} />
        <input value={f.securityTerm} onChange={(e) => set("securityTerm", e.target.value)} placeholder="Security term (e.g. 26-Week)" className={inputCls} data-testid={`batch-treasury-secterm-${loan.id}`} />
        <input value={f.repoCounterparty} onChange={(e) => set("repoCounterparty", e.target.value)} placeholder="Repo counterparty" className={inputCls} data-testid={`batch-treasury-cpty-${loan.id}`} />
        <input value={f.fiscalAgent} onChange={(e) => set("fiscalAgent", e.target.value)} placeholder="Fiscal agent (defaults to FRB St. Louis TCMM)" className={`${inputCls} md:col-span-2`} data-testid={`batch-treasury-agent-${loan.id}`} />
      </div>
      <div className="text-[10px] text-[#8A8A80] mb-2">Enter at least a CUSIP, Transaction Reference, or Repo Amount — the full rendered Treasury package will then bundle into this borrower's batch section.
        {(f.maturityDate || f.interestRate || f.highYield) && (
          <span className="text-[#7fe7c2]"> · Selected: {f.maturityDate ? `matures ${f.maturityDate}` : ""}{(f.interestRate || f.highYield) ? ` · ${f.interestRate || f.highYield}%` : ""}</span>
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(loan, f)} disabled={saving || !(f.cusip.trim() || f.txnRef.trim() || String(f.repoAmount).trim())}
          className="flex items-center gap-1.5 text-[11px] font-semibold bg-[#B8860B] hover:bg-[#d19f22] text-[#0A0A0A] rounded-md px-3 py-1.5 disabled:opacity-50" data-testid={`batch-treasury-save-${loan.id}`}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Treasury data
        </button>
        <button onClick={onCancel} className="text-[11px] text-[#8A8A80] hover:text-[#ECECE4] px-3 py-1.5">Cancel</button>
      </div>
    </div>
  );
}


function AccreditationForm({ bank, onSave, onCancel }) {
  const a = bank.accreditation || {};
  const [entityType, setEntityType] = useState(a.entity_type || "");
  const [category, setCategory] = useState(a.category || "qib_144a");
  const [exemption, setExemption] = useState(a.exemption || "144a");
  const [worth, setWorth] = useState(a.net_worth_or_aum || "");
  const [reps, setReps] = useState(!!a.reps_confirmed);
  const [signedName, setSignedName] = useState(a.signed_name || "");
  const save = (signed) => onSave(bank.id, { entity_type: entityType, category, exemption, net_worth_or_aum: worth, reps_confirmed: reps, signed_name: signedName, signed });
  return (
    <div className="bg-[#0d0d09] border border-[#E4C97A]/30 rounded-md px-3 py-3 mb-1" data-testid={`batch-accreditation-form-${bank.id}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#E4C97A] font-bold mb-2">Buyer accreditation — {bank.name}</div>
      <div className="grid md:grid-cols-2 gap-2 mb-2">
        <input value={entityType} onChange={(e) => setEntityType(e.target.value)} placeholder="Entity type (e.g. national bank, fund)"
          className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1.5 text-[11px] text-[#ECECE4]" data-testid={`batch-acc-entity-${bank.id}`} />
        <input value={worth} onChange={(e) => setWorth(e.target.value)} placeholder="Net worth / AUM attested"
          className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1.5 text-[11px] text-[#ECECE4]" data-testid={`batch-acc-worth-${bank.id}`} />
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1.5 text-[11px] text-[#ECECE4]" data-testid={`batch-acc-category-${bank.id}`}>
          <option value="qib_144a">QIB (Rule 144A)</option>
          <option value="accredited_501a">Accredited investor (Reg D 501(a))</option>
          <option value="institutional">Institutional</option>
          <option value="other">Other</option>
        </select>
        <select value={exemption} onChange={(e) => setExemption(e.target.value)}
          className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1.5 text-[11px] text-[#ECECE4]" data-testid={`batch-acc-exemption-${bank.id}`}>
          <option value="144a">Rule 144A</option>
          <option value="506b">Reg D 506(b)</option>
          <option value="506c">Reg D 506(c)</option>
          <option value="4a2">Section 4(a)(2)</option>
          <option value="regs">Regulation S</option>
        </select>
      </div>
      <label className="flex items-start gap-2 text-[10.5px] text-[#C9C9C0] mb-2 cursor-pointer">
        <input type="checkbox" checked={reps} onChange={(e) => setReps(e.target.checked)} className="mt-0.5" data-testid={`batch-acc-reps-${bank.id}`} />
        <span>Buyer represents it is acquiring for its own account for investment, is qualified under the category above, and understands the notes are not registered and are transfer-restricted.</span>
      </label>
      <div className="flex items-center gap-2 flex-wrap">
        <input value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="Authorized signer name"
          className="flex-1 min-w-[160px] bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1.5 text-[11px] text-[#ECECE4]" data-testid={`batch-acc-signer-${bank.id}`} />
        <button onClick={() => save(false)} className="text-[11px] rounded-md px-3 py-1.5 border border-[#2A2A22] text-[#8A8A80] hover:text-[#ECECE4]" data-testid={`batch-acc-save-${bank.id}`}>Save draft</button>
        <button disabled={!reps || !signedName.trim()} onClick={() => save(true)}
          className="text-[11px] font-semibold rounded-md px-3 py-1.5 bg-[#E4C97A] text-[#0A0A0A] hover:bg-[#f0d98a] disabled:opacity-40" data-testid={`batch-acc-sign-${bank.id}`}>Sign &amp; attest</button>
        <button onClick={onCancel} className="text-[11px] text-[#8A5A5A] hover:text-red-400 ml-auto" data-testid={`batch-acc-cancel-${bank.id}`}>Cancel</button>
      </div>
    </div>
  );
}
